import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Tabs, Button as AntdButton, ConfigProvider, DatePicker, Popover, Select, Modal, Input, Tag, Divider, Drawer, Tooltip, Checkbox, message } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { YEAR_PRESETS, isCustomYear, formatPeakDate } from "./timeframe";
import { BUILDING_BLOCK_LABELS } from "./schema";
import { extractAssumptions } from "./components/AssumptionsPanel";
import { RoadmapGrid } from "./components/RoadmapGrid";
import { BlockDetailView } from "./components/BlockDetailView";
import { PhaseIndicator } from "./components/PhaseIndicator";
import { AssumptionsPanel } from "./components/AssumptionsPanel";
import { GapsPanel } from "./components/GapsPanel";
import { QuestionsPanel } from "./components/QuestionsPanel";
import { VersionHistoryPanel } from "./components/VersionHistoryPanel";
import { ProgressReflectionPanel } from "./components/ProgressReflectionPanel";
import { useRoadmapViewMode } from "./hooks/useRoadmapViewMode";
import { PillToggle } from "components/ConsensusBuilder/ConsensusBuilderBar";
import { AttachmentBar } from "./components/AttachmentBar";
import { SessionStudioPanel } from "./components/SessionStudioPanel";
import type { SessionArtifact } from "./components/SessionStudioPanel";
import type {
  SessionData,
  ChatMessage,
  StreamChunk,
  AllBuildingBlocks,
  Gap,
  OutstandingQuestion,
  Attachment,
  Collaborator,
  CollaboratorAccessLevel,
  ResearchRun,
} from "./types";
import { isVisibleForPeriod } from "./types";
import { VojomoSymbol } from "./components/VojomoSymbol";
import {
  ArrowLeft,
  PanelRightOpen,
  PanelRightClose,
  PanelLeftOpen,
  PanelLeftClose,
  Loader2,
  BookCheck,
  CheckCircle2,
  FileText,
  AlertCircle,
  LayoutGrid,
  Lock,
  RefreshCw,
  Share2,
  Download,
  Printer,
  Clock,
  Copy,
  Check,
  Trash2,
  Pencil,
  CalendarDays,
  Upload,
  TrendingUp,
  Users,
  UserPlus,
  ChevronDown,
  ChevronUp,
  X,
  Settings,
  Library,
  Globe,
} from "lucide-react";
import { Converse, MemberMarker, MicRecorderProvider, ModusProvider, useModus, useAccount } from "@vojomo-core/shared";
import "./rb-globals.css";
import "./session-layout.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

type RightPanel = "roadmap" | "progress" | "studio";

function formatDateTime(dateStr: string | Date): string {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function RoadmapBuilderSessionInner() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const id = sessionId || "";
  const [searchParams] = useSearchParams();

  const { setModusChatContext } = useModus();

  const [session, setSession] = useState<SessionData | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingQueue, setPendingQueue] = useState<string[]>([]);
  const [, setStreamingContent] = useState("");
  const validPanels: RightPanel[] = ["roadmap", "progress", "studio"];
  const tabParam = searchParams.get("tab") as RightPanel | null;
  const [rightPanel, setRightPanel] = useState<RightPanel>(
    tabParam && validPanels.includes(tabParam) ? tabParam : "roadmap"
  );
  const [chatCollapsed, setChatCollapsed] = useState(false);
  // Solo/Team is one global mode for the whole roadmap (State 2 only), owned
  // by useRoadmapViewMode and fed to every surface. linkedBlocks is retired.
  const linkedBlocks: AllBuildingBlocks | null = null;
  const [roadmapCollapsed, setRoadmapCollapsed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [messagesSentSinceRefresh, setMessagesSentSinceRefresh] = useState(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTab, setShareModalTab] = useState<"link" | "invite">("link");
  const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);
  const [collabSettings, setCollabSettings] = useState({ teamMembersCanInvite: false, notifyOnJoin: true });
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  // Invite dropdown offers exactly two roles: Owner and Team Member. Legacy access
  // levels (view/comment/solo_contributor) remain valid in the data model and on
  // existing collaborator rows, but are no longer offered at invite time.
  const [inviteRoleGroup, setInviteRoleGroup] = useState<"owner" | "team_member">("team_member");
  // Derived: the actual access_level sent to the backend. Typed as the full union so
  // legacy-value comparisons elsewhere still type-check.
  const inviteRole: CollaboratorAccessLevel = inviteRoleGroup;
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState<"roadmap" | "chat" | "both">("both");
  const [exportView, setExportView] = useState<"solo" | "team" | "both">("both");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [periodLockedBlocks, setPeriodLockedBlocks] = useState<Record<string, { locked: boolean; periodKey: string; isActive: boolean }>>({});
  const [lockVersion, setLockVersion] = useState(0);
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [peakDateEdit, setPeakDateEdit] = useState<Dayjs | null>(null);
  const [yearStartMonthEdit, setYearStartMonthEdit] = useState(1);
  const [yearStartDayEdit, setYearStartDayEdit] = useState(1);
  const [isSavingTimeframe, setIsSavingTimeframe] = useState(false);
  const [studioArtifacts, setStudioArtifacts] = useState<SessionArtifact[]>([]);

  // ── Cross-tab confirmation state (lifted from panels) ──
  const [gapDrafts, setGapDrafts] = useState<Record<string, string>>({});
  const [questionDrafts, setQuestionDrafts] = useState<Record<string, string>>({});
  const [assumptionActions, setAssumptionActions] = useState<
    Record<string, { type: "approve" | "reject" | "modify"; modifiedValue?: string }>
  >({});
  const [isApplying, setIsApplying] = useState(false);

  // ── Roadmap sub-view and history drawer ──
  const [roadmapSubView, setRoadmapSubView] = useState<"grid" | "assumptions" | "gaps" | "questions">("grid");
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  // ── Block focus mode ──
  const [focusedBlockKey, setFocusedBlockKey] = useState<string | null>(null);
  const [focusedBlockAutoEdit, setFocusedBlockAutoEdit] = useState(false);

  // ── File attachments ──
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // ── Phase 4: contribution submission ──
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // ── Invite: contribution deadline + message ──
  const [inviteMessage, setInviteMessage] = useState("");

  // ── Session Settings drawer ──
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"advisors" | "teams">("advisors");
  const [advisorEmailInput, setAdvisorEmailInput] = useState("");
  const [advisorAddLoading, setAdvisorAddLoading] = useState(false);
  const [advisorAddError, setAdvisorAddError] = useState<string | null>(null);
  const [advisorRemovingId, setAdvisorRemovingId] = useState<string | null>(null);
  const [participantTeams, setParticipantTeams] = useState<{ id: string; name: string; members: { email: string; name?: string }[] }[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [teamAddLoading, setTeamAddLoading] = useState(false);
  const [teamDeleteId, setTeamDeleteId] = useState<string | null>(null);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [memberEmailInputs, setMemberEmailInputs] = useState<Record<string, string>>({});
  const [memberAddLoading, setMemberAddLoading] = useState<string | null>(null);
  // Research refresh
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [researchRefreshing, setResearchRefreshing] = useState(false);
  const [researchAdditionalContext, setResearchAdditionalContext] = useState("");
  const [researchRefreshStatus, setResearchRefreshStatus] = useState<"idle" | "started" | "error">("idle");

  const handleRenameStart = () => {
    if (!session) return;
    setTitleValue(session.companyName);
    setIsEditingTitle(true);
  };

  const handleRenameSave = async () => {
    const trimmed = titleValue.trim();
    if (!trimmed || !session || trimmed === session.companyName) {
      setIsEditingTitle(false);
      return;
    }
    setIsSavingTitle(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/rename`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName: trimmed }),
        credentials: "include",
      });
      if (res.ok) setSession((prev) => prev ? { ...prev, companyName: trimmed } : prev);
    } catch (e) {
      console.error("Failed to rename session", e);
    } finally {
      setIsSavingTitle(false);
      setIsEditingTitle(false);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleRenameSave();
    if (e.key === "Escape") setIsEditingTitle(false);
  };

  // ── Settings: advisors ──
  const loadParticipantTeams = async () => {
    setTeamsLoading(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/participant-teams`, { credentials: "include" });
      if (r.ok) setParticipantTeams(await r.json());
    } catch { /* ignore */ }
    finally { setTeamsLoading(false); }
  };

  const openSettings = (tab: "advisors" | "teams" = "advisors") => {
    setSettingsTab(tab);
    setAdvisorEmailInput("");
    setAdvisorAddError(null);
    setSettingsOpen(true);
    loadParticipantTeams();
  };

  const handleResearchRefresh = async () => {
    if (!id) return;
    setResearchRefreshing(true);
    setResearchRefreshStatus("idle");
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/refresh-research`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ additionalContext: researchAdditionalContext.trim() }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      setResearchRefreshStatus("started");
      setResearchAdditionalContext("");
      // Optimistically reset so the in-progress banner appears immediately
      setSession(prev => prev ? { ...prev, researchComplete: false, researchStartedAt: new Date().toISOString() } : prev);
      // Auto-close after a moment so user sees the "underway" state then returns to work
      setTimeout(() => setResearchModalOpen(false), 2200);
    } catch {
      setResearchRefreshStatus("error");
    } finally {
      setResearchRefreshing(false);
    }
  };

  const handleAddAdvisor = async () => {
    const email = advisorEmailInput.trim().toLowerCase();
    if (!email) return;
    setAdvisorAddLoading(true);
    setAdvisorAddError(null);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/advisors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setAdvisorAddError((err as { detail?: string }).detail || "Failed to add advisor");
      } else {
        const data = await r.json();
        setSession((prev) => prev ? { ...prev, advisors: data.advisors } : prev);
        setAdvisorEmailInput("");
      }
    } catch {
      setAdvisorAddError("Network error");
    } finally {
      setAdvisorAddLoading(false);
    }
  };

  const handleRemoveAdvisor = async (userId: string) => {
    setAdvisorRemovingId(userId);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/advisors/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (r.ok) {
        const data = await r.json();
        setSession((prev) => prev ? { ...prev, advisors: data.advisors } : prev);
      }
    } catch { /* ignore */ }
    finally { setAdvisorRemovingId(null); }
  };

  const handleAddTeam = async () => {
    const name = newTeamName.trim();
    if (!name) return;
    setTeamAddLoading(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/participant-teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
        credentials: "include",
      });
      if (r.ok) {
        const team = await r.json();
        setParticipantTeams((prev) => [...prev, team]);
        setNewTeamName("");
      }
    } catch { /* ignore */ }
    finally { setTeamAddLoading(false); }
  };

  const handleDeleteTeam = async (teamId: string) => {
    setTeamDeleteId(teamId);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/participant-teams/${teamId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setParticipantTeams((prev) => prev.filter((t) => t.id !== teamId));
    } catch { /* ignore */ }
    finally { setTeamDeleteId(null); }
  };

  const handleAddMember = async (teamId: string) => {
    const email = (memberEmailInputs[teamId] || "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const team = participantTeams.find((t) => t.id === teamId);
    if (!team) return;
    if (team.members.some((m) => m.email === email)) return;
    setMemberAddLoading(teamId);
    const updatedMembers = [...team.members, { email }];
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/participant-teams/${teamId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ members: updatedMembers }),
        credentials: "include",
      });
      if (r.ok) {
        const updated = await r.json();
        setParticipantTeams((prev) => prev.map((t) => t.id === teamId ? updated : t));
        setMemberEmailInputs((prev) => ({ ...prev, [teamId]: "" }));
      }
    } catch { /* ignore */ }
    finally { setMemberAddLoading(null); }
  };

  const handleRemoveMember = async (teamId: string, email: string) => {
    const team = participantTeams.find((t) => t.id === teamId);
    if (!team) return;
    const updatedMembers = team.members.filter((m) => m.email !== email);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/participant-teams/${teamId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ members: updatedMembers }),
        credentials: "include",
      });
      if (r.ok) {
        const updated = await r.json();
        setParticipantTeams((prev) => prev.map((t) => t.id === teamId ? updated : t));
      }
    } catch { /* ignore */ }
  };

  // PATCH /blocks writes to the caller's solo output, so build the payload from
  // the solo blocks — spreading team blocks here copies team content into solo.
  const handleBlockSave = async (blockKey: string, updatedBlock: any) => {
    if (!session) return;
    const baseBlocks = session.viewerOutput?.buildingBlocks ?? session.buildingBlocks;
    const updatedBlocks: AllBuildingBlocks = { ...baseBlocks, [blockKey]: updatedBlock };
    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/blocks`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ buildingBlocks: updatedBlocks }),
      credentials: "include",
    });
    if (res.ok) {
      setSession((prev) => {
        if (!prev) return prev;
        if (prev.viewerOutput) {
          return { ...prev, viewerOutput: { ...prev.viewerOutput, buildingBlocks: updatedBlocks } };
        }
        return { ...prev, buildingBlocks: updatedBlocks };
      });
    }
  };

  const handleToggleLock = async (blockKey: string, locked: boolean) => {
    if (!session) return;
    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/lock-block`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ blockKey, locked }),
      credentials: "include",
    });
    if (res.ok) {
      const updated = await res.json();
      setSession((prev) => prev ? { ...prev, lockedBlocks: updated.lockedBlocks } : prev);
    }
  };

  const handleValidateAssumption = async (blockKey: string, itemIndex: number, validated: boolean) => {
    if (!session) return;
    const block = ((session.viewerOutput?.buildingBlocks ?? session.buildingBlocks) as any)[blockKey];
    if (!block?.wmbt) return;
    const updatedBlock = {
      ...block,
      wmbt: block.wmbt.map((item: any, i: number) =>
        i === itemIndex ? { ...item, userValidated: validated } : item
      ),
    };
    await handleBlockSave(blockKey, updatedBlock);
  };

  const openTimeframePopover = () => {
    if (!session) return;
    setPeakDateEdit(session.peakDate ? dayjs(session.peakDate) : null);
    setYearStartMonthEdit(session.yearStartMonth ?? 1);
    setYearStartDayEdit(session.yearStartDay ?? 1);
    setTimeframeOpen(true);
  };

  const handleTimeframeSave = async () => {
    if (!session) return;
    setIsSavingTimeframe(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/timeframe`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          peakDate: peakDateEdit ? peakDateEdit.format("YYYY-MM-DD") : null,
          yearStartMonth: yearStartMonthEdit,
          yearStartDay: yearStartDayEdit,
        }),
        credentials: "include",
      });
      if (res.ok) {
        const updated = await res.json();
        // The /timeframe response is a team-session shape without viewerOutput;
        // preserve the caller's existing per-user output so solo-derived state
        // (e.g. the "Shared ✓" label) isn't clobbered until the next full GET.
        setSession((old) => (old ? { ...updated, viewerOutput: old.viewerOutput } : updated));
      }
    } catch (e) {
      console.error("Failed to save timeframe", e);
    } finally {
      setIsSavingTimeframe(false);
      setTimeframeOpen(false);
    }
  };

  // Initial session load
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setSessionLoading(true);
      try {
        const [sessionRes, datasourcesRes] = await Promise.all([
          fetch(`${SERVER_URL}/api/rb/sessions/${id}`, { credentials: "include" }),
          fetch(`${SERVER_URL}/api/rb/sessions/${id}/datasources`, { credentials: "include" }),
        ]);
        if (sessionRes.ok) {
          const data = await sessionRes.json();
          setSession(data);
        }
        if (datasourcesRes.ok) {
          const data: Record<string, unknown>[] = await datasourcesRes.json();
          setAttachments(
            data
              .filter((d) => d.source_type === "file_upload")
              .map((d) => ({
                id: d._id as string,
                filename: (d.filename as string) || "Attachment",
                sourceType: d.source_type as string,
                contentLength: typeof d.content === "string" ? (d.content as string).length : undefined,
                createdAt: d.created_at as string | undefined,
              }))
          );
        }
      } finally {
        setSessionLoading(false);
      }
    };
    load();
  }, [id]);

  // Fetch current-calendar-period lock state for objectives BBs (grid indicator)
  useEffect(() => {
    if (!id) return;
    const bbTypes: { bbType: string; blockKey: string }[] = [
      { bbType: "quarterly", blockKey: "quarterlyObjectives" },
      { bbType: "annual", blockKey: "oneYearObjectives" },
      { bbType: "three_year", blockKey: "threeYearObjectives" },
      { bbType: "peak_ambition", blockKey: "peakAmbitionObjectives" },
    ];
    Promise.all(
      bbTypes.map(({ bbType, blockKey }) =>
        fetch(`${SERVER_URL}/api/rb/sessions/${id}/bb/${bbType}/period-info`, { credentials: "include" })
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (!d) return { blockKey, locked: false, periodKey: "", isActive: false };
            const currentKey = d.currentQuarterKey || "";
            const activeKey = d.activeQuarterKey || "";
            const currentPeriod = (d.allQuarters || []).find((q: { period_key: string }) => q.period_key === currentKey);
            return {
              blockKey,
              locked: currentPeriod?.is_locked ?? false,
              periodKey: currentKey,
              isActive: currentKey === activeKey,
            };
          })
          .catch(() => ({ blockKey, locked: false, periodKey: "", isActive: false }))
      )
    ).then((results) => {
      const map: Record<string, { locked: boolean; periodKey: string; isActive: boolean }> = {};
      for (const { blockKey, ...rest } of results) map[blockKey] = rest;
      setPeriodLockedBlocks(map);
    });
  }, [id, session?.buildingBlocks, lockVersion]);

  // The roadmap grid always shows team-level data; linkedBlocks (the
  // "show the opposite mode" overlay) was driven by a parent-level
  // Solo/Team toggle that we removed. Solo/Team is now per-block —
  // handled inside ProseBBTab / ObjectivesBBTab — so the grid stays on
  // team and linkedBlocks remains null. Left declared so RoadmapGrid's
  // existing prop wiring keeps compiling.

  // Poll every 5s while research is in progress
  useEffect(() => {
    if (!session) return;
    if (session.researchComplete) return;
    // Use researchStartedAt (updated on every trigger) so manual re-runs work on old sessions
    const refTime = session.researchStartedAt || session.sessionStartedAt;
    const ageMs = Date.now() - new Date(refTime || 0).getTime();
    if (ageMs >= 10 * 60 * 1000) return; // 10 min safety net

    const interval = setInterval(async () => {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}`, {
        credentials: "include",
      });
      if (res.ok) setSession(await res.json());
    }, 5000);

    return () => clearInterval(interval);
  }, [id, session?.id, session?.researchComplete, session?.researchStartedAt]);

  // Pulls the full message list from the server. Used on session load and
  // after Apply — POST /assumptions, /gaps, /questions all insert server-side
  // context messages ("[Assumption decisions] ...", "[Gap responses] ...")
  // that the optimistic chat path never sees, so without a refetch they only
  // appear on full page reload.
  const loadMessages = useCallback(async () => {
    if (!id) return;
    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/messages`, {
      credentials: "include",
    });
    if (res.ok) {
      const loaded: ChatMessage[] = await res.json();
      setMessages(loaded);
      if (loaded.length > 0) {
        setModusChatContext({
          _id: id,
          pl_group_id: 0,
          agent: "rb",
          messages: loaded.map((m) => ({ role: m.role, content: m.content })),
        });
      }
    }
  }, [id]);

  // Load messages once session is available
  useEffect(() => {
    if (!session) return;
    loadMessages();
  }, [id, session?.id, loadMessages]);

  // Load alignment reports for Studio tab
  const loadAlignmentReports = useCallback(async () => {
    const [synthRes, alignRes] = await Promise.allSettled([
      fetch(`${SERVER_URL}/api/rb/sessions/${id}/progress-syntheses`, { credentials: "include" }),
      fetch(`${SERVER_URL}/api/rb/sessions/${id}/alignment-reports`, { credentials: "include" }),
    ]);

    type ProgressSynth = { id: string; reportMarkdown: string; generatedAt: string; periodType: string; periodKey: string; contributorNames: string[]; contributorCount: number };
    type AlignReport = { id: string; reportType?: string; periodType?: string; periodKey?: string; reportMarkdown: string; createdAt: string; generatedAt?: string; contributors: { name: string }[] };

    const synthDocs: ProgressSynth[] = synthRes.status === "fulfilled" && synthRes.value.ok ? await synthRes.value.json() : [];
    const alignDocs: AlignReport[] = alignRes.status === "fulfilled" && alignRes.value.ok ? await alignRes.value.json() : [];

    const formatWeekKey = (key: string): string => {
      // key = "YYYY-WXX" → "Mar 23–29, 2026"
      const m = key.match(/^(\d{4})-W(\d{2})$/);
      if (!m) return key;
      const [, year, week] = m;
      // ISO week Monday
      const jan4 = new Date(Number(year), 0, 4);
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (Number(week) - 1) * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(monday)}–${fmt(sunday)}, ${year}`;
    };

    const formatQbrKey = (key: string): string => {
      // key = "YYYY-QX" → "Q1 FY2026"
      const m = key.match(/^(\d{4})-Q(\d)$/);
      if (!m) return key;
      return `Q${m[2]} FY${m[1]}`;
    };

    const synthArtifacts: typeof studioArtifacts = synthDocs.map((r) => {
      const periodLabel = r.periodType === "weekly"
        ? `Weekly — ${formatWeekKey(r.periodKey)}`
        : `Quarterly — ${formatQbrKey(r.periodKey)}`;
      const runAt = r.generatedAt ? new Date(r.generatedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "";
      const names = r.contributorNames?.length ? r.contributorNames.join(", ") : `${r.contributorCount} contributor${r.contributorCount !== 1 ? "s" : ""}`;
      return {
        id: `synth-${r.id}`,
        title: `Progress: Team Alignment Report ${periodLabel}`,
        type: "text_document" as const,
        content: r.reportMarkdown,
        createdAt: r.generatedAt,
        source: `${names} · ${runAt}`,
        periodType: r.periodType as "weekly" | "qbr",
        periodKey: r.periodKey,
      };
    });

    const alignArtifacts: typeof studioArtifacts = alignDocs.map((r) => {
      const dateStr = r.generatedAt || r.createdAt;
      const runAt = dateStr ? new Date(dateStr).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "";
      const names = r.contributors?.map((c) => c.name).filter(Boolean).join(", ") || "";
      const bbLabelMap: Record<string, string> = {
        nqo_synthesis: "Quarterly Objectives",
        nqo_direct_edit: "Quarterly Objectives",
        annual_obj_synthesis: "1-Year Objectives",
        annual_obj_direct_edit: "1-Year Objectives",
        three_year_obj_synthesis: "3-Year Objectives",
        three_year_obj_direct_edit: "3-Year Objectives",
        peak_ambition_obj_synthesis: "Peak Ambition Objectives",
        peak_ambition_obj_direct_edit: "Peak Ambition Objectives",
        bb_vision_synthesis: "Vision",
        bb_vision_direct_edit: "Vision",
        bb_mission_synthesis: "Mission",
        bb_mission_direct_edit: "Mission",
        bb_peak_ambition_synthesis: "Peak Ambition",
        bb_peak_ambition_direct_edit: "Peak Ambition",
      };
      const periodLabelMap: Record<string, (pk: string) => string> = {
        nqo_synthesis: (pk) => { const m = pk.match(/^(\d{4})-Q(\d)$/); return m ? `Q${m[2]} ${m[1]}` : pk; },
        nqo_direct_edit: (pk) => { const m = pk.match(/^(\d{4})-Q(\d)$/); return m ? `Q${m[2]} ${m[1]}` : pk; },
        annual_obj_synthesis: (pk) => pk.replace("FY", "FY "),
        annual_obj_direct_edit: (pk) => pk.replace("FY", "FY "),
        three_year_obj_synthesis: (pk) => { const m = pk.match(/^3Y-(\d{4})-(\d{4})$/); return m ? `${m[1]}–${m[2]}` : pk; },
        three_year_obj_direct_edit: (pk) => { const m = pk.match(/^3Y-(\d{4})-(\d{4})$/); return m ? `${m[1]}–${m[2]}` : pk; },
        peak_ambition_obj_synthesis: (pk) => pk.replace("PA-", "Target: "),
        peak_ambition_obj_direct_edit: (pk) => pk.replace("PA-", "Target: "),
      };
      const bbLabel = bbLabelMap[r.reportType || ""] || "Roadmap";
      const periodFn = periodLabelMap[r.reportType || ""];
      const periodPrefix = periodFn && r.periodKey ? `${periodFn(r.periodKey)} ` : "";
      const title = `Roadmap: ${periodPrefix}${bbLabel} Team Alignment Report`;
      return {
        id: `align-${r.id}`,
        title,
        type: "text_document" as const,
        content: r.reportMarkdown,
        createdAt: dateStr || r.createdAt,
        source: names ? `${names} · ${runAt}` : runAt,
        periodType: r.reportType === "nqo_synthesis" ? "nqo" as const : (r.periodType as "weekly" | "qbr" | "nqo" | undefined),
        periodKey: r.periodKey,
      };
    });

    // Merge and sort newest first
    const all = [...synthArtifacts, ...alignArtifacts].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    setStudioArtifacts(all);
  }, [id]);

  const handleDeleteArtifact = async (artifactId: string) => {
    const rawId = artifactId.replace(/^(synth-|align-)/, "");
    const url = artifactId.startsWith("align-")
      ? `${SERVER_URL}/api/rb/sessions/${id}/alignment-reports/${rawId}`
      : `${SERVER_URL}/api/rb/sessions/${id}/progress-syntheses/${rawId}`;
    const res = await fetch(url, { method: "DELETE", credentials: "include" });
    if (res.ok) setStudioArtifacts((prev) => prev.filter((a) => a.id !== artifactId));
  };

  useEffect(() => {
    if (!session) return;
    loadAlignmentReports();
  }, [id, session?.id]);

  // Auto-collapse chat panel when navigating to Progress & Reflection
  useEffect(() => {
    if (rightPanel === "progress") {
      setChatCollapsed(true);
    }
  }, [rightPanel]);

  // Close popovers on outside click
  useEffect(() => {
    if (!isExportMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-popover]")) {
        setIsExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isExportMenuOpen]);

  const refreshSession = useCallback(async () => {
    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}`, {
      credentials: "include",
    });
    if (res.ok) setSession(await res.json());
  }, [id]);

  const handleStream = async (response: Response, syncToModus = false) => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";
    let fullContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const chunk: StreamChunk = JSON.parse(line.slice(6));
          if (chunk.content) {
            fullContent += chunk.content;
            let jsonStart = fullContent.indexOf("```buildingblocks");
            if (jsonStart < 0) {
              // Also detect raw JSON: { ... "buildingBlocks"
              const rawMatch = fullContent.search(/\{\s*"buildingBlocks"/);
              if (rawMatch >= 0) jsonStart = rawMatch;
            }
            const displayContent =
              jsonStart >= 0 ? fullContent.slice(0, jsonStart).trimEnd() : fullContent;
            setStreamingContent(displayContent);
            if (syncToModus && displayContent) {
              setModusChatContext((prev) => {
                if (!prev) return prev;
                const msgs = [...prev.messages];
                msgs[msgs.length - 1] = { role: "assistant", content: displayContent };
                return { ...prev, messages: msgs };
              });
            }
          }
          if (
            "buildingBlocks" in chunk ||
            "gaps" in chunk ||
            "outstandingQuestions" in chunk
          ) {
            setSession((old) => {
              if (!old) return old;
              const updated = { ...old };
              // currentPhase / researchComplete remain on the parent (roadmap-wide).
              if (chunk.buildingBlocks) {
                updated.currentPhase = chunk.phase ?? old.currentPhase;
                updated.researchComplete =
                  chunk.researchComplete ?? old.researchComplete;
              }
              // buildingBlocks / gaps / outstandingQuestions are per-user content:
              // mirror them onto viewerOutput when present so the read sites
              // (which prefer viewerOutput) pick up the streamed updates. Falls
              // back to the parent slice for legacy sessions with no viewerOutput.
              if (old.viewerOutput) {
                const nextViewer = { ...old.viewerOutput };
                if (chunk.buildingBlocks)
                  nextViewer.buildingBlocks = chunk.buildingBlocks;
                if ("gaps" in chunk)
                  nextViewer.gaps = chunk.gaps as Gap[];
                if ("outstandingQuestions" in chunk)
                  nextViewer.outstandingQuestions =
                    chunk.outstandingQuestions as OutstandingQuestion[];
                updated.viewerOutput = nextViewer;
              } else {
                if (chunk.buildingBlocks)
                  updated.buildingBlocks = chunk.buildingBlocks;
                if ("gaps" in chunk) updated.gaps = chunk.gaps as Gap[];
                if ("outstandingQuestions" in chunk)
                  updated.outstandingQuestions =
                    chunk.outstandingQuestions as OutstandingQuestion[];
              }
              return updated;
            });
          }
          if (chunk.done) {
            // Strip both fenced and raw JSON from the message content
            let cleanedContent = fullContent
              .replace(/```buildingblocks[\s\S]*?```/g, "")
              .replace(/\{\s*"buildingBlocks"[\s\S]*/g, "")
              .trim();
            const assistantMessage: ChatMessage = {
              id: Date.now() + 1,
              sessionId: id,
              role: "assistant",
              content: cleanedContent,
              createdAt: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
            setStreamingContent("");
            refreshSession();
            if (syncToModus) {
              const cleanContent = cleanedContent;
              setModusChatContext((prev) => {
                if (!prev) return prev;
                const msgs = [...prev.messages];
                msgs[msgs.length - 1] = { role: "assistant", content: cleanContent };
                return { ...prev, messages: msgs };
              });
            }
          }
        } catch {
          // Ignore malformed SSE lines
        }
      }
    }
  };

  // The RB chat renders ONLY from modusChatContext (Converse/useModus derives
  // latestVoMessage and the transcript from it). A streamed Vo reply must be
  // seeded as an assistant placeholder and synced via handleStream(syncToModus
  // = true); otherwise it lands only in the PDF-only `messages` array and never
  // appears in the chat. sendMessage seeds its own placeholder inline; the
  // recalculate / refresh triggers share this helper.
  const streamVoReply = async (response: Response) => {
    // Seed an assistant placeholder for the streamed reply to fill, constructing
    // the context if it doesn't exist yet (e.g. a session with no prior
    // messages) — mirrors sendMessage's seeding so handleStream(syncToModus =
    // true) has a slot to update. We deliberately do NOT toggle isStreaming:
    // recalculate/refresh stream a large hidden buildingblocks JSON after the
    // visible prose, so `done` arrives long (~90s) after Vo's reply is on
    // screen — driving the "Updating" indicator would leave it spinning that
    // whole time. The "..." placeholder is the inline progress cue instead.
    setModusChatContext((prev) => ({
      _id: id,
      pl_group_id: 0,
      agent: "rb",
      messages: [...(prev?.messages ?? []), { role: "assistant", content: "..." }],
    }));
    await handleStream(response, true);
  };

  const sendMessage = useCallback(
    async (content: string, _skipMsgAdd = false) => {
      if (!content.trim()) return;

      // If Vo is currently streaming a response, queue this message and show it
      // immediately in the chat — it will be sent automatically when streaming ends.
      if (isStreaming) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            sessionId: id,
            role: "user" as const,
            content: content.trim(),
            createdAt: new Date().toISOString(),
          },
        ]);
        setPendingQueue((prev) => [...prev, content.trim()]);
        return;
      }

      const userMessage: ChatMessage = {
        id: Date.now(),
        sessionId: id,
        role: "user",
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };

      if (!_skipMsgAdd) setMessages((prev) => [...prev, userMessage]);
      // Prime modus context so Converse shows the exchange
      setModusChatContext((prev) => ({
        _id: id,
        pl_group_id: 0,
        agent: "rb",
        messages: [
          ...(prev?.messages ?? []),
          { role: "user", content: content.trim() },
          { role: "assistant", content: "..." },
        ],
      }));
      setIsStreaming(true);
      setStreamingContent("");
      setMessagesSentSinceRefresh((n) => n + 1);

      try {
        const response = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: content.trim() }),
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to send message");
        await handleStream(response, true);
      } catch {
        setStreamingContent("");
      } finally {
        setIsStreaming(false);
      }
    },
    [isStreaming, id, setModusChatContext]
  );

  // Keep a ref to the latest sendMessage so the queue-drain effect always calls the fresh version.
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  // When streaming ends, automatically send any messages that were typed during the stream.
  useEffect(() => {
    if (isStreaming || pendingQueue.length === 0) return;
    const [next, ...rest] = pendingQueue;
    setPendingQueue(rest);
    sendMessageRef.current(next, true); // true = message already in list, skip re-adding
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, pendingQueue.length]);

  const handlePublishDraft = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (response.ok) refreshSession();
    } catch {
      console.error("Publish failed");
    }
  };

  const openShareModal = async (tab: "link" | "invite" = "link") => {
    setShareModalTab(tab);
    setShareModalOpen(true);
    if (!shareUrl && session?.shareToken) {
      setShareUrl(`${window.location.origin}/roadmap/view/${session.shareToken}`);
    }
    if (session?.userRole === "owner" || session?.userRole === "advisor" || !session?.userRole) {
      loadCollaborators();
    }
  };

  const loadCollaborators = async () => {
    setCollaboratorsLoading(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators`, {
        credentials: "include",
      });
      if (r.ok) setCollaborators(await r.json());
    } catch {
      /* ignore */
    } finally {
      setCollaboratorsLoading(false);
    }
  };

  // Load collaborators on mount so the State-2 view-toggle gate (and the
  // People-with-access list) reflect sent invites without needing the Share
  // modal to be opened. Owners/advisors can read the list; other roles would
  // 403 (harmless) and don't need it — their non-owner role already drives the
  // toggle gate.
  useEffect(() => {
    if (!id) return;
    if (session?.userRole === "owner" || session?.userRole === "advisor") {
      loadCollaborators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, session?.userRole]);

  const handleShare = async () => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/share`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed");
      const data = await r.json();
      const token = data.share_token || data.shareToken;
      const url =
        data.url ||
        (token ? `${window.location.origin}/roadmap/view/${token}` : null);
      setShareUrl(url);
      refreshSession();
    } catch {
      console.error("Share failed");
    }
  };

  const handleRevokeShare = async () => {
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/share`, {
        method: "DELETE",
        credentials: "include",
      });
      setShareUrl(null);
      refreshSession();
    } catch {
      console.error("Revoke failed");
    }
  };

  const handleSendInvite = async () => {
    const emails = inviteEmail.split(",").map((e) => e.trim()).filter(Boolean);
    if (!emails.length) return;
    setInviteLoading(true);
    setInviteError(null);
    try {
      const newCollabs: Collaborator[] = [];
      for (const email of emails) {
        const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email,
            accessLevel: inviteRole,
            ...(inviteMessage.trim() && { message: inviteMessage.trim() }),
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(`${email}: ${err.detail || "Failed to send invite"}`);
        }
        newCollabs.push(await r.json());
      }
      setCollaborators((prev) => [...prev, ...newCollabs]);
      setInviteEmail("");
      setInviteRoleGroup("team_member");
      setInviteMessage("");
    } catch (e: unknown) {
      setInviteError(e instanceof Error ? e.message : "Failed to send invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveCollaborator = async (collabId: string) => {
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators/${collabId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setCollaborators((prev) => prev.filter((c) => c.id !== collabId));
    } catch {
      console.error("Failed to remove collaborator");
    }
  };

  const handleLeaveSession = async () => {
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/leave`, {
        method: "POST",
        credentials: "include",
      });
      window.location.href = "/roadmap/build_roadmap";
    } catch {
      console.error("Failed to leave session");
    }
  };

  const handlePromoteCollaborator = async (collabId: string) => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators/${collabId}/promote`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        const updated = await r.json();
        setCollaborators((prev) => prev.map((c) => c.id === collabId ? updated : c));
      }
    } catch {
      console.error("Failed to promote collaborator");
    }
  };

  const handleDemoteCollaborator = async (collabId: string) => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators/${collabId}/demote`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        const updated = await r.json();
        setCollaborators((prev) => prev.map((c) => c.id === collabId ? updated : c));
      }
    } catch {
      console.error("Failed to demote collaborator");
    }
  };

  const handleUpdateCollaboratorRole = async (collabId: string, newRole: CollaboratorAccessLevel) => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/collaborators/${collabId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accessLevel: newRole }),
      });
      if (r.ok) {
        const updated = await r.json();
        setCollaborators((prev) => prev.map((c) => c.id === collabId ? updated : c));
      }
    } catch {
      console.error("Failed to update collaborator role");
    }
  };

  // ── Phase 4 handlers ──────────────────────────────────────────────────────

  // "Commit all" — commits every non-blank solo block at once (submit-contribution
  // now sets commitState="committed" on content-bearing blocks + notifies the owner).
  const handleSubmitContribution = async () => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/submit-contribution`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        // Endpoint returns { success, viewerOutput, committedCount } — merge the
        // caller's own per-user output (now committed) back into session state so
        // the button + block badges reflect the committed state immediately.
        const data = await r.json();
        if (data?.viewerOutput) {
          setSession((old) => (old ? { ...old, viewerOutput: data.viewerOutput } : old));
        }
        message.success("Your view is committed — the owner can synthesize it whenever.");
      } else {
        let detail = "Couldn't commit your view. Please try again.";
        try {
          const err = await r.json();
          if (err?.detail) detail = err.detail;
        } catch { /* non-JSON error body */ }
        message.error(detail);
      }
    } catch {
      message.error("Couldn't commit your view. Please try again.");
    } finally {
      setIsSubmitting(false);
      setSubmitConfirmOpen(false);
    }
  };

  // Per-block solo commit/uncommit — the solo mirror of the team block lock.
  const handleToggleCommit = async (blockKey: string, committed: boolean) => {
    if (!id) return;
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/outputs/commit-block`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blockKey, committed }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data?.viewerOutput) {
          setSession((old) => (old ? { ...old, viewerOutput: data.viewerOutput } : old));
        }
      } else {
        let detail = "Couldn't update commit state. Please try again.";
        try {
          const err = await r.json();
          if (err?.detail) detail = err.detail;
        } catch { /* non-JSON */ }
        message.error(detail);
      }
    } catch {
      message.error("Couldn't update commit state. Please try again.");
    }
  };


  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleExportJSON = () => {
    if (!session) return;
    // JSON ships both views unconditionally — it's a developer / archival
    // format, so providing both Solo (the viewer's private workspace)
    // and Team (the synthesized consensus) at once is the safe default.
    const data = JSON.stringify(
      {
        companyName: session.companyName,
        draftVersion: session.draftVersion,
        exportedAt: new Date().toISOString(),
        team: {
          buildingBlocks: session.buildingBlocks ?? {},
          gaps: session.gaps ?? [],
          outstandingQuestions: session.outstandingQuestions ?? [],
        },
        solo: session.viewerOutput ? {
          owner: session.viewerOutput.owner,
          buildingBlocks: session.viewerOutput.buildingBlocks ?? {},
          gaps: session.viewerOutput.gaps ?? [],
          outstandingQuestions: session.viewerOutput.outstandingQuestions ?? [],
        } : null,
      },
      null,
      2
    );
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${session.companyName.toLowerCase().replace(/\s+/g, "-")}-roadmap.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePdfHtml = (
    mode: "roadmap" | "chat" | "both",
    viewMode: "solo" | "team" | "both" = "both",
  ): string => {
    if (!session) return "";
    const name = session.companyName;
    const version = session.draftVersion || 0;
    const now = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Pick blocks/gaps/questions based on the requested view. Solo falls
    // back to team if viewerOutput is missing (legacy data).
    const teamData = {
      blocks: (session.buildingBlocks ?? {}) as any,
      gaps: (session.gaps ?? []) as Gap[],
      questions: (session.outstandingQuestions ?? []) as OutstandingQuestion[],
    };
    const soloData = session.viewerOutput ? {
      blocks: (session.viewerOutput.buildingBlocks ?? {}) as any,
      gaps: (session.viewerOutput.gaps ?? []) as Gap[],
      questions: (session.viewerOutput.outstandingQuestions ?? []) as OutstandingQuestion[],
    } : null;

    const catLabels: Record<string, string> = {
      financial: "Financial",
      market_share: "Market Share",
      customer: "Customer",
      custom: "Company-Specific",
    };
    const blockDefs = [
      { key: "vision", num: "01", title: "Vision", isObj: false, isPA: false },
      { key: "mission", num: "02", title: "Mission", isObj: false, isPA: false },
      { key: "peakAmbition", num: "03", title: "Peak Ambition", isObj: false, isPA: true },
      { key: "peakAmbitionObjectives", num: "04", title: "Peak Ambition Objectives", isObj: true, isPA: false },
      { key: "threeYearObjectives", num: "05", title: "3-Year Objectives", isObj: true, isPA: false },
      { key: "oneYearObjectives", num: "06", title: "1-Year Objectives", isObj: true, isPA: false },
      { key: "quarterlyObjectives", num: "07", title: "Quarterly Objectives", isObj: true, isPA: false },
    ];

    const esc = (s: string) =>
      (s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const fmtDate = (iso: string) => {
      try {
        return new Date(iso).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch {
        return iso;
      }
    };
    const mdToHtml = (text: string) => {
      let h = esc(text)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`(.+?)`/g, "<code>$1</code>")
        .replace(/^### (.+)$/gm, "<h4>$1</h4>")
        .replace(/^## (.+)$/gm, "<h3>$1</h3>")
        .replace(/^# (.+)$/gm, "<h2>$1</h2>")
        .replace(/^[-•*] (.+)$/gm, "<li>$1</li>")
        .replace(/\n{2,}/g, "</p><p>")
        .replace(/\n/g, "<br>");
      h = h.replace(/(<li>(?:.|\n)*?<\/li>)/g, "<ul>$1</ul>");
      return h.startsWith("<") ? h : `<p>${h}</p>`;
    };

    const renderRoadmap = (
      data: { blocks: any; gaps: Gap[]; questions: OutstandingQuestion[] },
      label?: string,
    ) => {
      const { blocks, gaps: dGaps, questions: dQuestions } = data;
      const subtitle = label
        ? `Draft v${version} · ${label} View · ${now}`
        : `Draft v${version} · ${now}`;
      let html = `<div class="section"><h2 class="part-title">Strategic Roadmap${label ? ` — ${label}` : ""}</h2><p class="part-sub">${subtitle}</p></div>`;
      for (const def of blockDefs) {
        const b = blocks[def.key] || {};
        const isEmpty =
          b.status === "empty" ||
          (!b.draft && !b.headline && !(b.wmbt && b.wmbt.length));
        html += `<div class="block${isEmpty ? " block-empty" : ""}"><div class="bh"><span class="bnum">${def.num}</span><h3 class="btitle">${def.title}</h3><span class="bstatus s-${b.status || "empty"}">${b.status || "empty"}</span></div>`;
        if (isEmpty) {
          html += `<p class="empty">Not yet populated</p>`;
        } else if (def.isPA) {
          if (b.headline) html += `<div class="field"><span class="fl">Headline</span><p class="fv strong">${esc(b.headline)}</p></div>`;
          if (b.date) html += `<div class="field"><span class="fl">Target Date</span><p class="fv">${esc(b.date)}</p></div>`;
          if (b.pressRelease) html += `<div class="field"><span class="fl">Press Release</span><p class="fv">${esc(b.pressRelease)}</p></div>`;
          if (b.businessOutcome) html += `<div class="field"><span class="fl">Business Outcome</span><p class="fv">${esc(b.businessOutcome)}</p></div>`;
          if (b.worldImpact) html += `<div class="field"><span class="fl">World Impact</span><p class="fv">${esc(b.worldImpact)}</p></div>`;
          if (!b.headline && b.draft) html += `<p class="bt">${esc(b.draft)}</p>`;
        } else if (def.isObj && b.wmbt && b.wmbt.length > 0) {
          const groups: Record<string, any[]> = {};
          const order: string[] = [];
          for (const item of b.wmbt) {
            if (!item.category) continue;
            if (!groups[item.category]) {
              groups[item.category] = [];
              order.push(item.category);
            }
            groups[item.category].push(item);
          }
          for (const cat of order) {
            const items = groups[cat];
            const obj = items.find((i: any) => i.categoryObjective)?.categoryObjective;
            html += `<div class="wg"><div class="wcat">${catLabels[cat] || cat}</div>`;
            if (obj) html += `<div class="wobj"><span class="fl">Objective</span> ${esc(obj)}</div>`;
            html += `<ul class="wl">`;
            for (const item of items.filter((i: any) => i.label)) {
              html += `<li class="wi"><span class="wlabel">${esc(item.label)}</span>`;
              if (item.value) html += ` <span class="wval">— ${esc(item.value)}</span>`;
              if (item.isAssumption) html += ` <span class="badge ba">assumption</span>`;
              if (item.userValidated) html += ` <span class="badge bv">validated</span>`;
              if (item.isAssumption && item.assumptionExplanation)
                html += `<div class="anote">${esc(item.assumptionExplanation)}</div>`;
              html += `</li>`;
            }
            html += `</ul></div>`;
          }
          if (!order.length && b.draft) html += `<p class="bt">${esc(b.draft)}</p>`;
        } else if (b.draft) {
          html += `<p class="bt">${esc(b.draft)}</p>`;
        }
        const bGaps = (dGaps || []).filter(
          (g: Gap) => g.blockKey === def.key && g.status !== "filled"
        );
        if (bGaps.length)
          html += `<div class="bgaps"><span class="fl">Gaps to fill</span><ul class="gl">${bGaps
            .map((g: Gap) => `<li>${esc(g.description)}</li>`)
            .join("")}</ul></div>`;
        html += `</div>`;
      }
      const openQs = (dQuestions || []).filter(
        (q: OutstandingQuestion) => q.status === "pending"
      );
      const answeredQs = (dQuestions || []).filter(
        (q: OutstandingQuestion) => q.status === "answered"
      );
      if (openQs.length || answeredQs.length) {
        html += `<div class="block"><div class="bh"><h3 class="btitle">Outstanding Questions</h3></div>`;
        if (openQs.length)
          html += `<div class="field"><span class="fl">Open</span><ul class="ql">${openQs
            .map((q: OutstandingQuestion) => `<li>${esc(q.question)}</li>`)
            .join("")}</ul></div>`;
        if (answeredQs.length)
          html += `<div class="field"><span class="fl">Answered</span><ul class="ql">${answeredQs
            .map(
              (q: OutstandingQuestion) =>
                `<li><strong>${esc(q.question)}</strong>${
                  q.userAnswer ? `<div class="qa">${esc(q.userAnswer)}</div>` : ""
                }</li>`
            )
            .join("")}</ul></div>`;
        html += `</div>`;
      }
      return html;
    };

    const renderChat = () => {
      if (!messages.length)
        return `<div class="section"><h2 class="part-title">Chat Transcript</h2><p class="empty">No messages yet.</p></div>`;
      let html = `<div class="section"><h2 class="part-title">Chat Transcript with Vo</h2><p class="part-sub">${messages.length} messages · Exported ${now}</p></div>`;
      for (const msg of messages) {
        const isUser = msg.role === "user";
        html += `<div class="msg ${isUser ? "muser" : "mvo"}"><div class="mh"><span class="mrole">${
          isUser ? "You" : "Vo"
        }</span><span class="mtime">${fmtDate(msg.createdAt)}</span></div><div class="mbody">${
          isUser
            ? `<p>${esc(msg.content).replace(/\n/g, "<br>")}</p>`
            : mdToHtml(msg.content)
        }</div></div>`;
      }
      return html;
    };

    const css = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;line-height:1.65;color:#1a1a1a;background:#fff}
.cover{display:flex;flex-direction:column;justify-content:center;min-height:100vh;padding:60pt 72pt;page-break-after:always}
.cover-logo{font-size:12pt;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:#802a88;margin-bottom:48pt;font-family:Arial,sans-serif}
.cover-title{font-size:34pt;line-height:1.2;color:#1a1a1a;margin-bottom:10pt}
.cover-sub{font-size:13pt;color:#6b7280;font-family:Arial,sans-serif;margin-bottom:6pt}
.cover-meta{font-size:9.5pt;color:#9ca3af;margin-top:48pt;padding-top:14pt;border-top:1pt solid #e5e7eb;font-family:Arial,sans-serif}
.content{max-width:170mm;margin:0 auto;padding:20pt 0 40pt}
.section{margin-bottom:20pt;padding-bottom:10pt;border-bottom:2pt solid #2c1561}
.part-title{font-size:20pt;color:#2c1561;margin-bottom:3pt}
.part-sub{font-size:9.5pt;color:#6b7280;font-family:Arial,sans-serif}
.block{border:1pt solid #e5e7eb;border-radius:5pt;padding:13pt 15pt;margin-bottom:10pt;page-break-inside:avoid}
.block-empty{background:#fafafa;border-style:dashed}
.bh{display:flex;align-items:baseline;gap:7pt;margin-bottom:9pt;flex-wrap:wrap}
.bnum{font-size:7.5pt;font-family:'Courier New',monospace;color:#9ca3af;font-weight:700;padding-top:1pt}
.btitle{font-size:13pt;font-weight:700;color:#1a1a1a;flex:1}
.bstatus{font-size:7.5pt;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:.05em;padding:1.5pt 5pt;border-radius:3pt}
.s-complete{background:#d1fae5;color:#065f46}.s-partial{background:#fef3c7;color:#92400e}.s-empty{background:#f3f4f6;color:#9ca3af}
.bt{font-size:10.5pt;line-height:1.7;color:#374151;white-space:pre-wrap}
.empty{font-size:9.5pt;color:#9ca3af;font-style:italic;font-family:Arial,sans-serif}
.field{margin-bottom:9pt}
.fl{font-size:7.5pt;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:.08em;color:#9ca3af;font-weight:700;display:block;margin-bottom:2pt}
.fv{font-size:10.5pt;line-height:1.6;color:#374151;white-space:pre-wrap}.strong{font-weight:700;font-size:11.5pt;color:#1a1a1a}
.wg{margin-bottom:11pt;padding:9pt 11pt;background:#f9fafb;border-radius:4pt}
.wcat{font-size:7.5pt;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:.1em;font-weight:700;color:#6b7280;margin-bottom:5pt}
.wobj{font-size:10pt;font-style:italic;color:#374151;margin-bottom:7pt}
.wl{list-style:none;padding:0}
.wi{display:flex;align-items:baseline;flex-wrap:wrap;gap:4pt;padding:4pt 0;border-bottom:.5pt solid #e5e7eb;font-size:10pt}
.wi:last-child{border-bottom:none}
.wlabel{font-weight:700;color:#1a1a1a}.wval{color:#6b7280}
.badge{font-size:7pt;font-family:Arial,sans-serif;padding:1pt 4pt;border-radius:2pt;border:.5pt solid}
.ba{color:#92400e;border-color:#d97706;background:#fef3c7}.bv{color:#065f46;border-color:#059669;background:#d1fae5}
.anote{width:100%;font-size:8.5pt;font-style:italic;color:#92400e;margin-top:2pt;padding-left:8pt}
.bgaps{margin-top:9pt;padding-top:7pt;border-top:.5pt solid #f3f4f6}
.gl{list-style:none;padding:0;margin-top:3pt}
.gl li{font-size:9pt;color:#b45309;padding:2pt 0 2pt 12pt;position:relative;font-family:Arial,sans-serif}
.gl li::before{content:"⚠";position:absolute;left:0;font-size:7.5pt;top:3pt}
.ql{list-style:none;padding:0;margin-top:4pt}
.ql li{font-size:10pt;padding:5pt 0;border-bottom:.5pt solid #f3f4f6}
.ql li:last-child{border-bottom:none}
.qa{font-size:9.5pt;color:#6b7280;padding-left:12pt;margin-top:2pt;font-style:italic}
.msg{margin-bottom:14pt;padding:11pt 13pt;border-radius:5pt;page-break-inside:avoid}
.muser{background:#f9fafb;border:1pt solid #e5e7eb}
.mvo{background:#faf5ff;border:1pt solid #e9d5ff;border-left:3pt solid #7c3aed}
.mh{display:flex;align-items:center;gap:7pt;margin-bottom:7pt}
.mrole{font-family:Arial,sans-serif;font-size:8.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280}
.mvo .mrole{color:#7c3aed}
.mtime{font-family:Arial,sans-serif;font-size:8pt;color:#9ca3af}
.mbody{font-size:10.5pt;line-height:1.7;color:#374151}
.mbody p{margin-bottom:5pt}.mbody p:last-child{margin-bottom:0}
.mbody ul{padding-left:16pt;margin:3pt 0 7pt}.mbody li{margin-bottom:2pt}
.mbody strong{font-weight:700;color:#1a1a1a}.mbody em{font-style:italic}
.mbody code{font-family:'Courier New',monospace;font-size:8.5pt;background:#f3f4f6;padding:1pt 3pt;border-radius:2pt}
.mbody h2{font-size:13pt;margin:9pt 0 3pt;color:#2c1561}
.mbody h3{font-size:11pt;margin:7pt 0 3pt;color:#2c1561}
.mbody h4{font-size:10pt;margin:5pt 0 2pt;font-style:italic}
.pb{page-break-before:always}
.action-bar{position:fixed;top:0;left:0;right:0;z-index:100;background:#fff;border-bottom:1.5pt solid #e5e7eb;padding:10pt 24pt;display:flex;align-items:center;gap:12pt;font-family:Arial,sans-serif;box-shadow:0 1px 4px rgba(0,0,0,.06)}
.action-bar-logo{font-size:10pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#802a88}
.action-bar-title{font-size:10pt;color:#6b7280;flex:1}
.action-bar-btn{background:#2c1561;color:#fff;border:none;border-radius:6pt;padding:7pt 16pt;font-size:10pt;font-weight:600;cursor:pointer;font-family:Arial,sans-serif;letter-spacing:.02em}
.action-bar-btn:hover{background:#3d1f80}
.action-bar-hint{font-size:8.5pt;color:#9ca3af}
body{padding-top:48pt}
@media print{.action-bar{display:none!important}body{padding-top:0}.cover{min-height:100vh}}`;

    const includeRoadmap = mode === "roadmap" || mode === "both";
    const includeChat = mode === "chat" || mode === "both";
    const modeLabel =
      mode === "both"
        ? "Roadmap + Chat Transcript"
        : mode === "roadmap"
        ? "Strategic Roadmap"
        : "Chat Transcript";

    return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${esc(
      name
    )} — ${modeLabel}</title><style>${css}</style></head><body>
<div class="action-bar">
  <span class="action-bar-logo">Vojomo</span>
  <span class="action-bar-title">${esc(name)} · ${modeLabel}${
    version ? ` · v${version}` : ""
  }</span>
  <button class="action-bar-btn" onclick="window.print()">Save as PDF</button>
  <span class="action-bar-hint">Cmd+P</span>
</div>
<div class="cover">
  <div class="cover-logo">Vojomo</div>
  <h1 class="cover-title">${esc(name)}</h1>
  <p class="cover-sub">${modeLabel}${version ? ` · Draft v${version}` : ""}</p>
  <div class="cover-meta">
    <p>Generated ${now} · Vojomo Roadmap Builder</p>
    ${session.domain ? `<p>URL: ${esc(session.domain)}</p>` : ""}
  </div>
</div>
<div class="content">
  ${(() => {
    if (!includeRoadmap) return "";
    if (viewMode === "team") return renderRoadmap(teamData);
    if (viewMode === "solo") return renderRoadmap(soloData ?? teamData, "Solo");
    // viewMode === "both"
    if (!soloData) return renderRoadmap(teamData);
    return renderRoadmap(soloData, "Solo")
      + '<div class="pb"></div>'
      + renderRoadmap(teamData, "Team");
  })()}
  ${includeRoadmap && includeChat ? '<div class="pb"></div>' : ""}
  ${includeChat ? renderChat() : ""}
</div>
</body></html>`;
  };

  const handleExportPDF = (
    mode: "roadmap" | "chat" | "both",
    viewMode: "solo" | "team" | "both" = "both",
  ) => {
    if (!session) return;
    const html = generatePdfHtml(mode, viewMode);
    const win = window.open("", "_blank");
    if (!win) {
      alert("Pop-up blocked — please allow pop-ups for this site to export.");
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  const handleAssumptionsProcessed = (blocks: AllBuildingBlocks) => {
    // In solo mode the per-user output is the source of truth — mirror the
    // server's write target. Falls back to the parent slice when no
    // viewerOutput exists (legacy sessions).
    setSession((old) => {
      if (!old) return old;
      if (old.viewerOutput) {
        return { ...old, viewerOutput: { ...old.viewerOutput, buildingBlocks: blocks } };
      }
      return { ...old, buildingBlocks: blocks };
    });
  };

  const refreshAttachments = async () => {
    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/datasources`, {
      credentials: "include",
    });
    if (res.ok) {
      const data: Record<string, unknown>[] = await res.json();
      setAttachments(
        data
          .filter((d) => d.source_type === "file_upload")
          .map((d) => ({
            id: d._id as string,
            filename: (d.filename as string) || "Attachment",
            sourceType: d.source_type as string,
            contentLength: typeof d.content === "string" ? (d.content as string).length : undefined,
            createdAt: d.created_at as string | undefined,
          }))
      );
    }
  };

  // Stage files locally — they upload when the user sends a message.
  // Also fixes empty filenames from clipboard pastes.
  const stageFiles = (files: File[]) => {
    const fixed = files.map((f) => {
      if (f.name) return f;
      const subtype = f.type.split("/")[1] || "file";
      return new File([f], `pasted-${subtype}`, { type: f.type });
    });
    setStagedFiles((prev) => [...prev, ...fixed]);
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/upload`, {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({})) as { detail?: string };
          throw new Error(err.detail || `Failed to upload ${file.name}`);
        }
      }
      await refreshAttachments();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/datasources/${attachmentId}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (err) {
      console.error("Failed to delete attachment:", err);
      await refreshAttachments();
    }
  };

  const handleImportRoadmap = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text) as Record<string, unknown>;
      if (!data.buildingBlocks) {
        alert("Invalid roadmap file: missing buildingBlocks field.");
        return;
      }
      if (!confirm("This will replace the current roadmap content. Continue?")) return;
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (res.ok) {
        const updated = await res.json() as SessionData;
        setSession(updated);
      }
    } catch {
      alert("Failed to import roadmap: invalid JSON file.");
    }
  };

  const handleApplyAllConfirmations = async () => {
    const gapsToApply = Object.entries(gapDrafts).filter(([, v]) => v.trim());
    const questionsToApply = Object.entries(questionDrafts);
    const actionsToApply = Object.entries(assumptionActions);
    if (!gapsToApply.length && !questionsToApply.length && !actionsToApply.length) return;

    setIsApplying(true);
    // Snapshot the keys being submitted so we only clear those — any new work typed during
    // the apply is preserved.
    const gapKeys = new Set(gapsToApply.map(([k]) => k));
    const questionKeys = new Set(questionsToApply.map(([k]) => k));
    const actionKeys = new Set(actionsToApply.map(([k]) => k));

    try {
      const tasks: Promise<void>[] = [];

      if (gapsToApply.length) {
        tasks.push(
          fetch(`${SERVER_URL}/api/rb/sessions/${id}/gaps`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              updates: gapsToApply.map(([gapId, userResponse]) => ({ gapId, userResponse })),
            }),
            credentials: "include",
          })
            .then((r) => {
              if (!r.ok) throw new Error("Failed to apply gaps");
              return r.json();
            })
            .then((data) => {
              handleGapsUpdated(data.gaps);
              setGapDrafts((prev) => {
                const next = { ...prev };
                gapKeys.forEach((k) => delete next[k]);
                return next;
              });
            }),
        );
      }

      if (questionsToApply.length) {
        tasks.push(
          fetch(`${SERVER_URL}/api/rb/sessions/${id}/questions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              answers: questionsToApply.map(([questionId, userAnswer]) => ({
                questionId,
                userAnswer,
              })),
            }),
            credentials: "include",
          })
            .then((r) => {
              if (!r.ok) throw new Error("Failed to apply questions");
              return r.json();
            })
            .then((data) => {
              handleQuestionsUpdated(data.questions || data.outstandingQuestions || []);
              setQuestionDrafts((prev) => {
                const next = { ...prev };
                questionKeys.forEach((k) => delete next[k]);
                return next;
              });
            }),
        );
      }

      if (actionsToApply.length && session) {
        // AssumptionsPanel reads soloBlocks (viewerOutput.buildingBlocks for
        // solo, parent for team), so confirmation indices are scoped to the
        // same slice the panel rendered from.
        const allAssumptions = extractAssumptions(soloBlocks);
        const actions = actionsToApply.map(([key, action]) => {
          const [blockKey, indexStr] = key.split("-");
          const itemIndex = parseInt(indexStr);
          const assumption = allAssumptions.find(
            (a) => a.blockKey === blockKey && a.itemIndex === itemIndex,
          );
          return {
            blockKey,
            itemIndex,
            type: action.type,
            modifiedValue: action.modifiedValue,
            label:
              assumption?.item.label || assumption?.item.categoryObjective || "",
            blockLabel:
              BUILDING_BLOCK_LABELS[blockKey as keyof typeof BUILDING_BLOCK_LABELS]?.label ||
              blockKey,
          };
        });
        tasks.push(
          fetch(`${SERVER_URL}/api/rb/sessions/${id}/assumptions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ actions }),
            credentials: "include",
          })
            .then((r) => {
              if (!r.ok) throw new Error("Failed to apply assumptions");
              return r.json();
            })
            .then((data) => {
              handleAssumptionsProcessed(data.buildingBlocks);
              setAssumptionActions((prev) => {
                const next = { ...prev };
                actionKeys.forEach((k) => delete next[k]);
                return next;
              });
            }),
        );
      }

      await Promise.all(tasks);
      // Pull the server-inserted context messages (`[Assumption decisions]`,
      // `[Gap responses]`, `[Question answers]`) into the local chat history
      // BEFORE the recalculate stream starts, so the user sees their decisions
      // reflected immediately instead of after refresh.
      await loadMessages();
      handleRecalculate("User has confirmed gaps, questions, and assumptions.");
    } catch (error) {
      console.error("Failed to apply confirmations:", error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleGapsUpdated = (gaps: Gap[]) => {
    // Solo: mirror to viewerOutput (where the backend writes for solo sessions).
    // Team / legacy: mirror to the parent slice.
    setSession((old) => {
      if (!old) return old;
      if (old.viewerOutput) {
        return { ...old, viewerOutput: { ...old.viewerOutput, gaps } };
      }
      return { ...old, gaps };
    });
  };

  const handleQuestionsUpdated = (questions: OutstandingQuestion[]) => {
    setSession((old) => {
      if (!old) return old;
      if (old.viewerOutput) {
        return {
          ...old,
          viewerOutput: { ...old.viewerOutput, outstandingQuestions: questions },
        };
      }
      return { ...old, outstandingQuestions: questions };
    });
  };

  const handleSubmitGap = async (gapId: string, value: string) => {
    if (!value.trim()) return;
    const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/gaps`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: [{ gapId, userResponse: value }] }),
      credentials: "include",
    });
    if (r.ok) {
      const data = await r.json();
      handleGapsUpdated(data.gaps);
      setGapDrafts((prev) => { const n = { ...prev }; delete n[gapId]; return n; });
    }
  };

  const handleSubmitQuestion = async (questionId: string, value: string) => {
    if (!value.trim()) return;
    const r = await fetch(`${SERVER_URL}/api/rb/sessions/${id}/questions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers: [{ questionId, userAnswer: value }] }),
      credentials: "include",
    });
    if (r.ok) {
      const data = await r.json();
      handleQuestionsUpdated(data.questions || data.outstandingQuestions || []);
      setQuestionDrafts((prev) => { const n = { ...prev }; delete n[questionId]; return n; });
    }
  };

  const handleRecalculate = async (changeContext?: string) => {
    setStreamingContent("");
    try {
      const response = await fetch(
        `${SERVER_URL}/api/rb/sessions/${id}/recalculate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            changeContext: changeContext || "User has updated roadmap variables.",
          }),
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Recalculation failed");
      await streamVoReply(response);
    } catch {
      setStreamingContent("");
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRightPanel("roadmap");
    setStreamingContent("");
    try {
      const response = await fetch(
        `${SERVER_URL}/api/rb/sessions/${id}/refresh`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sinceTimestamp: lastRefreshedAt?.toISOString() }),
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Refresh failed");
      await streamVoReply(response);
      setLastRefreshedAt(new Date());
      setMessagesSentSinceRefresh(0);
    } catch {
      setStreamingContent("");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Cross-block aggregator panels (Assumptions, Gaps, Questions, Progress
  // Reflection) follow the ONE global Solo/Team mode via `activeBlocks`:
  // solo reads the viewer's per-user output (session.viewerOutput), team reads
  // the synthesized session consensus. In State 1 (single user) the mode is
  // forced solo, so a solo user works against their own roadmap.
  // Account read lifted above the early returns so the view-mode hook runs
  // unconditionally (rules of hooks). currentUserId is session-independent.
  const { vojomoAccount } = useAccount();
  const accountAny = vojomoAccount as unknown as Record<string, unknown> | null;
  const currentUserId = accountAny ? String(accountAny["_id"] ?? "") : "";

  // State 2 = an invite has been sent (owner) OR the viewer is a non-owner
  // (definitionally invited) OR a session advisor exists. The owner's own
  // RoadmapOutput exists from creation, so viewerOutput/outputs are NOT valid
  // State-2 signals — do not use them.
  const canShowToggle =
    (session?.userRole != null && session.userRole !== "owner") ||
    collaborators.length > 0 ||
    (session?.advisors?.length ?? 0) > 0;
  const { viewMode, setViewMode, showViewToggle } = useRoadmapViewMode(
    id,
    currentUserId,
    canShowToggle,
  );

  const teamBlocks: AllBuildingBlocks = session?.buildingBlocks ?? ({} as AllBuildingBlocks);
  const soloBlocks: AllBuildingBlocks = (session?.viewerOutput?.buildingBlocks
    ?? session?.buildingBlocks
    ?? ({} as AllBuildingBlocks));

  // A non-owner member with an editable solo slice = a "Contributor" who commits
  // their own view. Owners/advisors synthesize instead; pure viewers have nothing
  // to commit. A block has content if it has draft text, WMBT items, or a headline.
  const isContributor =
    session?.userRole === "team_member" || session?.userRole === "solo_contributor";
  const _blockHasContent = (b: unknown): boolean => {
    if (!b || typeof b !== "object") return false;
    const blk = b as { draft?: string; wmbt?: unknown[]; headline?: string };
    return Boolean(
      (blk.draft && blk.draft.trim()) ||
      (Array.isArray(blk.wmbt) && blk.wmbt.length > 0) ||
      (blk.headline && blk.headline.trim()),
    );
  };
  const soloHasContent = Object.values(soloBlocks).some(_blockHasContent);
  // "All committed" = every content-bearing solo block is committed (used by the
  // "Commit all" header button's done-state). commitState defaults to draft.
  const soloAllCommitted =
    soloHasContent &&
    Object.values(soloBlocks).every(
      (b) => !_blockHasContent(b) ||
        (b as { commitState?: string }).commitState === "committed",
    );

  // Cross-block panels follow the one global mode. Solo reads the viewer's
  // per-user output (viewerOutput); Team reads the parent session consensus.
  // Solo falls back to team for legacy data with no viewerOutput.
  const activeBlocks: AllBuildingBlocks = viewMode === "team" ? teamBlocks : soloBlocks;
  const gaps: Gap[] =
    viewMode === "team"
      ? (session?.gaps ?? [])
      : (session?.viewerOutput?.gaps ?? session?.gaps ?? []);
  const outstandingQuestions: OutstandingQuestion[] =
    viewMode === "team"
      ? (session?.outstandingQuestions ?? [])
      : (session?.viewerOutput?.outstandingQuestions ?? session?.outstandingQuestions ?? []);
  const openGapsCount = gaps.filter((g) => g.status === "open").length;
  const pendingQuestionsCount = outstandingQuestions.filter(
    (q) => q.status === "pending"
  ).length;

  const assumptionCount = (() => {
    const blocks = activeBlocks;
    let count = 0;
    const keys = [
      "peakAmbitionObjectives",
      "threeYearObjectives",
      "oneYearObjectives",
      "quarterlyObjectives",
    ] as const;
    for (const key of keys) {
      const block = blocks[key];
      if (block?.wmbt) {
        count += block.wmbt.filter((item) => item.isAssumption).length;
      }
    }
    return count;
  })();

  const totalPendingConfirmations =
    Object.values(gapDrafts).filter((v) => v.trim()).length +
    Object.keys(questionDrafts).length +
    Object.keys(assumptionActions).length;

  // Research is "actively happening" only if research_complete is false AND
  // the last research trigger is recent (< 10 min). This prevents stuck sessions
  // from showing the banner forever (data bug: research_complete never reset to true).
  const RESEARCH_TIMEOUT_MS = 10 * 60 * 1000;
  const researchRefTime = session?.researchStartedAt || session?.sessionStartedAt;
  const isResearchingNow =
    session !== null &&
    !session.researchComplete &&
    !!researchRefTime &&
    Date.now() - new Date(researchRefTime).getTime() < RESEARCH_TIMEOUT_MS;

  const wmbtMissing =
    session?.researchComplete &&
    soloBlocks.peakAmbition?.headline &&
    soloBlocks.peakAmbitionObjectives?.status === "empty";

  // ── Loading state ──
  if (sessionLoading) {
    return (
      <div className="rb-session-root" style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <Loader2 className="animate-spin" style={{ width: 28, height: 28, color: "#6366f1" }} />
          <p style={{ fontSize: 13, color: "#94a3b8" }}>Loading session...</p>
        </div>
      </div>
    );
  }

  // ── Not found ──
  if (!session) {
    return (
      <div className="rb-session-root" style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>Session not found</p>
          <Link to="/roadmap/myroadmaps">
            <AntdButton size="small">
              <ArrowLeft style={{ width: 14, height: 14, marginRight: 6 }} />
              Back to roadmaps
            </AntdButton>
          </Link>
        </div>
      </div>
    );
  }

  // ── Advisor check (for Progress tab features) ──
  // (account read + currentUserId were lifted above the early returns for the view-mode hook)
  // Dev-only: ?viewAs=team_member forces the Team Member experience (localhost only)
  const viewAsOverride = window.location.hostname === "localhost" ? searchParams.get("viewAs") : null;
  const isAdvisorOrOwner = viewAsOverride === "team_member"
    ? false
    : (session.userRole === "owner" ||
       session.userRole === "advisor" ||
       Boolean(accountAny?.["is_advisor"]) ||
       session.advisors.some((a) => a.userId === currentUserId));

  // ── Studio visibility gate — per-period visibility ──
  // The visibility toggle is the SINGLE control for all team-synthesized data per period.
  // Default-deny: only show artifacts whose specific period key is explicitly "open".
  const visibleStudioArtifacts = isAdvisorOrOwner
    ? studioArtifacts
    : studioArtifacts.filter((a) => {
        if (a.periodType && a.periodKey) {
          return isVisibleForPeriod(session.visibilityMap, a.periodType, a.periodKey);
        }
        return true; // Non-progress artifacts (e.g. roadmap alignment reports) pass through
      });

  // ── Roadmap tab items ──
  const tabItems = [
    {
      key: "roadmap",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <LayoutGrid className="rb-tab-icon" />
          Roadmap
        </span>
      ),
      children: (
        <div className="flex flex-col h-full">
          {/* Secondary nav strip */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-white shrink-0" style={{ borderColor: "#f1f5f9" }}>
            <div className="flex items-center gap-0">
              {(["grid", "assumptions", "gaps", "questions"] as const).map((view, i) => {
                const labels = { grid: "View", assumptions: "Assumptions", gaps: "Gaps", questions: "Questions" };
                const counts = {
                  grid: null,
                  assumptions: assumptionCount,
                  gaps: openGapsCount,
                  questions: pendingQuestionsCount,
                };
                const isActive = roadmapSubView === view;
                return (
                  <span key={view} className="flex items-center">
                    {i > 0 && <span className="text-slate-300 mx-2 text-[11px] select-none">|</span>}
                    <button
                      onClick={() => setRoadmapSubView(view)}
                      className={`border-0 bg-transparent outline-none cursor-pointer p-0 text-[12px] font-medium transition-colors ${
                        isActive
                          ? "text-primary"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {labels[view]}
                      {counts[view] !== null && counts[view]! > 0 && (
                        <span className={`ml-1 text-[10px] font-semibold ${isActive ? "text-primary" : "text-slate-400"}`}>
                          {counts[view]}
                        </span>
                      )}
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {showViewToggle && (
                <PillToggle value={viewMode} onChange={setViewMode} testId="rb-global-view-toggle" />
              )}
              {/* History icon button */}
              <button
                className="border-0 bg-transparent outline-none cursor-pointer p-0 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setHistoryDrawerOpen(true)}
                title="Version history"
              >
                <Clock className="h-3 w-3" />
                {session.draftVersion && session.draftVersion > 0 ? (
                  <span className="text-[11px] font-medium">v{session.draftVersion}</span>
                ) : null}
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 min-h-0 overflow-auto">
            {roadmapSubView === "grid" ? (
              focusedBlockKey ? (
                <BlockDetailView
                  blockKey={focusedBlockKey}
                  buildingBlocks={teamBlocks}
                  soloBuildingBlocks={soloBlocks}
                  viewMode={viewMode}
                  showViewToggle={showViewToggle}
                  sessionId={id}
                  peakDate={session.peakDate}
                  yearStartMonth={session.yearStartMonth}
                  yearStartDay={session.yearStartDay}
                  gaps={gaps}
                  autoEdit={focusedBlockAutoEdit}
                  onBack={() => { setFocusedBlockKey(null); setFocusedBlockAutoEdit(false); }}
                  onSessionRefresh={() => {
                    fetch(`${SERVER_URL}/api/rb/sessions/${id}`, { credentials: "include" })
                      .then((r) => r.ok ? r.json() : null)
                      .then((s) => { if (s) setSession(s); });
                  }}
                  isAdvisorOrOwner={isAdvisorOrOwner}
                  currentUserId={currentUserId}
                  userRole={session.userRole}
                  onReportCreated={loadAlignmentReports}
                  onLockChange={() => {
                    setLockVersion((v) => v + 1);
                    fetch(`${SERVER_URL}/api/rb/sessions/${id}`, { credentials: "include" })
                      .then((r) => r.ok ? r.json() : null)
                      .then((s) => { if (s) setSession((prev) => prev ? { ...prev, lockedBlocks: s.lockedBlocks } : prev); });
                  }}
                  onSendToMainChat={sendMessage}
                />
              ) : (
                <RoadmapGrid
                  buildingBlocks={teamBlocks}
                  viewerBlocks={soloBlocks}
                  viewMode={viewMode}
                  visibilityMap={session.visibilityMap}
                  showViewToggle={showViewToggle}
                  linkedBlocks={linkedBlocks}
                  companyName={session.companyName}
                  sessionId={id}
                  peakDate={session.peakDate}
                  yearStartMonth={session.yearStartMonth}
                  yearStartDay={session.yearStartDay}
                  isAdvisorOrOwner={isAdvisorOrOwner}
                  currentUserId={currentUserId}
                  userRole={session.userRole}
                  onFocusBlock={(key, opts) => {
                    setFocusedBlockKey(key);
                    setFocusedBlockAutoEdit(!!opts?.autoEdit);
                  }}
                  onSessionRefresh={refreshSession}
                  lockedBlocks={session.lockedBlocks}
                  periodLockedBlocks={periodLockedBlocks}
                  onToggleLock={isAdvisorOrOwner ? handleToggleLock : undefined}
                  onToggleCommit={handleToggleCommit}
                  onVisibilityChange={(updatedMap) => {
                    setSession((prev) => prev ? { ...prev, visibilityMap: updatedMap } : prev);
                    loadAlignmentReports();
                  }}
                  onReportCreated={loadAlignmentReports}
                  onSendToMainChat={sendMessage}
                />
              )
            ) : roadmapSubView === "assumptions" ? (
              <AssumptionsPanel
                buildingBlocks={activeBlocks}
                actions={assumptionActions}
                onActionSet={(key, action) => {
                  setAssumptionActions((prev) => {
                    if (action === null) {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    }
                    return { ...prev, [key]: action };
                  });
                }}
                onValidateAssumption={handleValidateAssumption}
              />
            ) : roadmapSubView === "gaps" ? (
              <GapsPanel
                gaps={gaps}
                sessionId={id}
                onGapsUpdated={handleGapsUpdated}
                drafts={gapDrafts}
                onDraftChange={(gapId, value) =>
                  setGapDrafts((prev) => {
                    if (value === "") {
                      const next = { ...prev };
                      delete next[gapId];
                      return next;
                    }
                    return { ...prev, [gapId]: value };
                  })
                }
                onSubmitGap={handleSubmitGap}
              />
            ) : (
              <QuestionsPanel
                questions={outstandingQuestions}
                sessionId={id}
                onQuestionsUpdated={handleQuestionsUpdated}
                drafts={questionDrafts}
                onDraftChange={(questionId, value) =>
                  setQuestionDrafts((prev) => {
                    if (value === "") {
                      const next = { ...prev };
                      delete next[questionId];
                      return next;
                    }
                    return { ...prev, [questionId]: value };
                  })
                }
                onSubmitQuestion={handleSubmitQuestion}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      key: "progress",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <TrendingUp className="rb-tab-icon" />
          Progress
        </span>
      ),
      children: (
        <ProgressReflectionPanel
          sessionId={id}
          // Follow the global Solo/Team mode like every other cross-block panel:
          // solo reads the viewer's own objectives (viewerOutput), team reads the
          // synthesized consensus. State 1 (single user) is forced solo upstream,
          // so a solo user assesses progress against their own roadmap.
          buildingBlocks={activeBlocks}
          yearStartMonth={session.yearStartMonth}
          yearStartDay={session.yearStartDay}
          isAdvisorOrOwner={isAdvisorOrOwner}
          currentUserId={currentUserId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showViewToggle={showViewToggle}
          companyName={session.companyName}
          visibilityMap={session.visibilityMap}
          defaultProgressTab={searchParams.get("progressTab") ?? undefined}
          onReportCreated={loadAlignmentReports}
          onSendToMainChat={sendMessage}
          onVisibilityChange={(updatedMap) => {
            setSession((prev) => prev ? { ...prev, visibilityMap: updatedMap } : prev);
            loadAlignmentReports();
          }}
        />
      ),
    },
    {
      key: "studio",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Library className="rb-tab-icon" />
          Studio
        </span>
      ),
      children: (
        <SessionStudioPanel
          artifacts={visibleStudioArtifacts}
          sessionId={id}
          companyName={session?.companyName}
          onDeleteArtifact={isAdvisorOrOwner ? handleDeleteArtifact : undefined}
        />
      ),
    },
  ];

  // ── Chat panel ──
  const chatPanel = (
    <div
      className={`rb-chat-panel${isDraggingOver ? " rb-chat-panel--drop-active" : ""}`}
      style={{ height: "100%" }}
      onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false); }}
      onDrop={(e) => { e.preventDefault(); setIsDraggingOver(false); stageFiles(Array.from(e.dataTransfer.files)); }}
      onPaste={(e) => { if (e.clipboardData.files.length > 0) { e.preventDefault(); stageFiles(Array.from(e.clipboardData.files)); } }}
    >
      <div className="rb-chat-shell">
        <div className="rb-panel-header">
          <div className="rb-panel-header-left">
            <span className="rb-panel-title">Vo</span>
          </div>
          <button
            className="icon-button-muted"
            onClick={() => setChatCollapsed(true)}
            title="Collapse Vo panel"
          >
            <PanelLeftClose className="icon-16" />
          </button>
        </div>
        <div className="rb-chat-content">
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <Converse
              size={200}
              fontSizeOverride="14px"
              orbFontSizeOverride="9px"
              showTranscriptIcon={true}
              placeHolder="Tell Vo about your company, vision, or ambitions..."
              onProcess={async (transcript: string) => {
                if (stagedFiles.length > 0) {
                  const toUpload = [...stagedFiles];
                  setStagedFiles([]);
                  await handleFileUpload(toUpload);
                }
                await sendMessage(transcript);
              }}
            />
          </div>
          <AttachmentBar
            attachments={attachments}
            stagedFiles={stagedFiles}
            isUploading={isUploading}
            uploadError={uploadError}
            onFilesSelected={stageFiles}
            onDelete={handleDeleteAttachment}
            onRemoveStaged={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
          />
        </div>
      </div>
    </div>
  );

  // ── Roadmap / doc panel ──
  const roadmapPanel = (
    <div className="rb-doc-panel" style={{ position: "relative" }}>
      {/* Research in progress banner — only shown while actively researching (< 10 min) */}
      {isResearchingNow && (
        <div className="rb-research-banner">
          <Loader2 className="animate-spin" style={{ width: 12, height: 12, flexShrink: 0 }} />
          Research in progress — your roadmap will auto-update with richer data in about 2 minutes
        </div>
      )}

      {/* Refresh timestamp */}
      {lastRefreshedAt && (
        <div className="rb-refresh-bar">
          <Clock style={{ width: 10, height: 10 }} />
          Refreshed {formatTimeAgo(lastRefreshedAt)}
          {messagesSentSinceRefresh > 0 && (
            <span style={{ color: "#d97706", fontWeight: 500, marginLeft: 4 }}>
              · {messagesSentSinceRefresh} new{" "}
              {messagesSentSinceRefresh === 1 ? "message" : "messages"} since refresh
            </span>
          )}
        </div>
      )}

      <Tabs
        className="rb-doc-tabs"
        activeKey={rightPanel}
        onChange={(key) => {
          setRightPanel(key as RightPanel);
        }}
        tabBarExtraContent={{
          right: (
            <div className="rb-tab-extra">
              <button
                className="icon-button-muted"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title={
                  messagesSentSinceRefresh > 0
                    ? "Refresh roadmap with new conversation info"
                    : wmbtMissing
                    ? "Build objective layers now"
                    : "Refresh roadmap with full conversation context"
                }
              >
                {isRefreshing ? (
                  <Loader2 className="icon-14 animate-spin" />
                ) : (
                  <div style={{ position: "relative" }}>
                    <RefreshCw className="icon-14" />
                    {(messagesSentSinceRefresh > 0 || wmbtMissing) && (
                      <span
                        style={{
                          position: "absolute",
                          top: -1,
                          right: -1,
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#f59e0b",
                        }}
                      />
                    )}
                  </div>
                )}
              </button>
              <button
                className="icon-button-muted"
                onClick={() => { setResearchModalOpen(true); setResearchRefreshStatus("idle"); }}
                title="Refresh roadmap with latest research"
              >
                <Globe className="icon-14" />
              </button>
              {isStreaming && (
                <span style={{ fontSize: 10, color: "#94a3b8", display: "flex", alignItems: "center", gap: 3 }}>
                  <Loader2 className="animate-spin" style={{ width: 10, height: 10 }} />
                  Updating
                </span>
              )}
              <button
                className="icon-button-muted"
                onClick={() => setRoadmapCollapsed(true)}
                title="Collapse roadmap panel"
              >
                <PanelRightClose className="icon-14" />
              </button>
            </div>
          ),
        }}
        items={tabItems}
      />
      {totalPendingConfirmations > 0 && (
        <div className="rb-apply-bar">
          <span className="rb-apply-bar-label">
            {totalPendingConfirmations} pending change{totalPendingConfirmations !== 1 ? "s" : ""}
            {isApplying && (
              <Loader2
                style={{ width: 11, height: 11, marginLeft: 6, display: "inline-block", verticalAlign: "middle", animation: "spin 1s linear infinite" }}
              />
            )}
          </span>
          <AntdButton
            type="primary"
            size="small"
            onClick={handleApplyAllConfirmations}
            disabled={isApplying}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <Check style={{ width: 12, height: 12 }} />
            Apply all
          </AntdButton>
        </div>
      )}
      {/* History Drawer */}
      <Drawer
        title={
          <span className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4" />
            Version History
            {session.draftVersion && session.draftVersion > 0 && (
              <span className="text-xs font-normal text-muted-foreground">v{session.draftVersion}</span>
            )}
          </span>
        }
        placement="right"
        width={400}
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        styles={{ body: { padding: 0 } }}
        getContainer={false}
        style={{ position: "absolute" }}
      >
        <VersionHistoryPanel
          sessionId={id}
          companyName={session.companyName}
          currentBlocks={session.buildingBlocks}
          lastPublishedAt={session.publishedAt}
        />
      </Drawer>
    </div>
  );

  // ── Main render ──
  return (
    <div className="rb-session-root">
      {/* ── Header ── */}
      <header className="rb-session-header">
        <div className="rb-session-header-left">
          <Link to="/roadmap/myroadmaps" title="Back to My Roadmaps">
            <button className="icon-button-muted">
              <ArrowLeft className="icon-16" />
            </button>
          </Link>
          <VojomoSymbol style={{ width: 18, height: 18, flexShrink: 0 }} />
          {isEditingTitle ? (
            <input
              className="rb-session-title-input"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleRenameSave}
              onKeyDown={handleRenameKeyDown}
              autoFocus
              disabled={isSavingTitle}
            />
          ) : (
            <span
              className="rb-session-title rb-session-title-editable"
              onClick={handleRenameStart}
              title="Click to rename"
            >
              {session.companyName}
              {isSavingTitle
                ? <Loader2 className="inline h-3 w-3 ml-1.5 animate-spin opacity-50" />
                : <Pencil className="rename-hint inline h-3 w-3 ml-1.5" />
              }
            </span>
          )}
          <Popover
            open={timeframeOpen}
            onOpenChange={(open) => { if (open) openTimeframePopover(); else setTimeframeOpen(false); }}
            trigger="click"
            placement="bottomLeft"
            content={
              <div style={{ width: 260 }} className="space-y-3 p-1">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Target peak date</label>
                  <DatePicker
                    picker="month"
                    placeholder="e.g. Dec 2030"
                    value={peakDateEdit}
                    onChange={setPeakDateEdit}
                    format="MMM YYYY"
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Year definition</label>
                  <Select
                    style={{ width: "100%" }}
                    value={
                      isCustomYear({ month: yearStartMonthEdit, day: yearStartDayEdit })
                        ? "custom"
                        : `${yearStartMonthEdit}-${yearStartDayEdit}`
                    }
                    onChange={(val) => {
                      if (val === "custom") return;
                      const [m, d] = val.split("-").map(Number);
                      setYearStartMonthEdit(m);
                      setYearStartDayEdit(d);
                    }}
                    options={[
                      ...YEAR_PRESETS.map(p => ({
                        value: `${p.month}-${p.day}`,
                        label: p.label,
                      })),
                      { value: "custom", label: "Custom…" },
                    ]}
                  />
                </div>
                <AntdButton
                  type="primary"
                  size="small"
                  block
                  loading={isSavingTimeframe}
                  onClick={handleTimeframeSave}
                >
                  Save
                </AntdButton>
              </div>
            }
          >
            {session.peakDate ? (
              <span className="rb-peak-date-chip rb-peak-date-chip--set" title="Edit timeframe">
                <CalendarDays className="rb-peak-date-icon" />
                {formatPeakDate(session.peakDate)}
                {isSavingTimeframe
                  ? <Loader2 className="rename-hint inline h-3 w-3 ml-1" style={{ animation: "spin 1s linear infinite" }} />
                  : <Pencil className="rename-hint rb-peak-date-icon" />
                }
              </span>
            ) : (
              <span className="rb-peak-date-chip rb-peak-date-chip--ghost" title="Add target date">
                <CalendarDays className="rb-peak-date-icon" />
                Add target date
              </span>
            )}
          </Popover>
          <PhaseIndicator
            phase={session.currentPhase}
            sessionStartedAt={session.sessionStartedAt}
            researchStartedAt={session.researchStartedAt}
            researchComplete={session.researchComplete}
          />
          {session.researchComplete && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                fontWeight: 500,
                color: "#065f46",
                background: "#d1fae5",
                borderRadius: 999,
                padding: "2px 8px",
                flexShrink: 0,
              }}
            >
              <CheckCircle2 style={{ width: 11, height: 11 }} />
              Research complete
            </span>
          )}
        </div>

        <div className="rb-session-header-right">
          <MemberMarker />
          {isContributor && (
            <AntdButton
              size="small"
              type={soloAllCommitted ? "default" : "primary"}
              disabled={!soloHasContent}
              style={{ height: 30, fontSize: 12, gap: 5 }}
              onClick={() => setSubmitConfirmOpen(true)}
              title={
                !soloHasContent
                  ? "Add some content to your view before committing it"
                  : "Commit every block of your view at once — commits carry a stronger 'settled view' tag; your content is available to the team either way"
              }
            >
              <Lock style={{ width: 13, height: 13 }} />
              {soloAllCommitted ? "All committed ✓" : "Commit all"}
            </AntdButton>
          )}
          {session.researchComplete && (
            <>
              <AntdButton
                size="small"
                type={
                  session.draftVersion && session.draftVersion > 0
                    ? "default"
                    : "primary"
                }
                style={{ height: 30, fontSize: 12, gap: 5 }}
                onClick={handlePublishDraft}
                title={
                  session.draftVersion && session.draftVersion > 0 && session.publishedAt
                    ? `Last published: ${formatDateTime(session.publishedAt)}`
                    : undefined
                }
              >
                <BookCheck style={{ width: 13, height: 13 }} />
                {session.draftVersion && session.draftVersion > 0
                  ? `Re-publish (v${session.draftVersion})`
                  : "Publish draft"}
              </AntdButton>

              <AntdButton
                size="small"
                type="text"
                style={{ height: 30, fontSize: 12, gap: 5 }}
                onClick={() => openShareModal("invite")}
                title="Share & invite collaborators"
              >
                <Users style={{ width: 13, height: 13 }} />
                Share
              </AntdButton>

              {(session.userRole === "owner" || session.userRole === "advisor") && (
                <AntdButton
                  size="small"
                  type="text"
                  style={{ height: 30, fontSize: 12, gap: 5 }}
                  onClick={() => openSettings("advisors")}
                  title="Session settings"
                >
                  <Settings style={{ width: 13, height: 13 }} />
                  Settings
                </AntdButton>
              )}

              <div style={{ position: "relative" }}>
                <AntdButton
                  size="small"
                  type="text"
                  style={{ height: 30, fontSize: 12, gap: 5 }}
                  onClick={() => setIsExportMenuOpen((v) => !v)}
                  title="Export roadmap"
                >
                  <Download style={{ width: 13, height: 13 }} />
                  Export
                </AntdButton>
                {isExportMenuOpen && (
                  <div
                    data-popover
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "calc(100% + 6px)",
                      zIndex: 50,
                      width: 176,
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      boxShadow: "0 4px 16px rgba(15,23,42,0.10)",
                      padding: "4px 0",
                    }}
                  >
                    {[
                      { icon: Printer, label: "Print", action: () => { window.print(); setIsExportMenuOpen(false); } },
                      { icon: Download, label: "Download PDF…", action: () => { setIsExportMenuOpen(false); setShowExportModal(true); } },
                      { icon: FileText, label: "Download JSON", action: () => { handleExportJSON(); setIsExportMenuOpen(false); } },
                      { icon: Upload, label: "Import roadmap…", action: () => { setIsExportMenuOpen(false); importInputRef.current?.click(); } },
                    ].map(({ icon: Icon, label, action }) => (
                      <button
                        key={label}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 12px",
                          fontSize: 12,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "left",
                          color: "#0f172a",
                        }}
                        onClick={action}
                      >
                        <Icon style={{ width: 13, height: 13, color: "#64748b" }} />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* ── Read-only banner for collaborators ── */}
      {session.collaboratorRole && (
        <div style={{ background: "#fef3c7", borderBottom: "1px solid #fde68a", padding: "6px 16px", display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <AlertCircle style={{ width: 13, height: 13, color: "#d97706", flexShrink: 0 }} />
          <span style={{ color: "#92400e" }}>
            You're viewing this roadmap as a <strong>{session.collaboratorRole === "view" ? "reviewer" : session.collaboratorRole === "comment" ? "reviewer" : session.collaboratorRole === "team_member" ? "team member" : "contributor"}</strong>.
            {session.collaboratorRole === "solo_contributor" && session.linkedSessionId && (
              <span> Your contribution is in a separate session.</span>
            )}
          </span>
        </div>
      )}

      {/* ── Body ── */}
      <div className="rb-page-body">
        {chatCollapsed && (
          <div
            className="rb-side-rail"
            onClick={() => setChatCollapsed(false)}
            title="Open Vo"
          >
            <div className="rb-rail-content">
              <PanelLeftOpen className="icon-16" />
              <span className="rb-rail-label">Vo</span>
            </div>
          </div>
        )}

        <div className="rb-page-main">
          {!chatCollapsed && !roadmapCollapsed ? (
            <PanelGroup direction="horizontal" className="rb-resizable-group">
              <Panel defaultSize={45} minSize={20} className="rb-panel">
                {chatPanel}
              </Panel>
              <PanelResizeHandle className="resize-handle" />
              <Panel defaultSize={55} minSize={25} className="rb-panel">
                {roadmapPanel}
              </Panel>
            </PanelGroup>
          ) : chatCollapsed ? (
            <div className="rb-panel" style={{ height: "100%" }}>
              {roadmapPanel}
            </div>
          ) : (
            <div style={{ height: "100%", display: "flex", justifyContent: "center" }}>
              <div className="rb-panel" style={{ width: "100%", maxWidth: "clamp(600px, 58vw, 920px)", height: "100%" }}>
                {chatPanel}
              </div>
            </div>
          )}
        </div>

        {roadmapCollapsed && (
          <div
            className="rb-side-rail"
            onClick={() => setRoadmapCollapsed(false)}
            title="Open roadmap"
          >
            <div className="rb-rail-content">
              <PanelRightOpen className="icon-16" />
              <span className="rb-rail-label">Roadmap</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Hidden import input ── */}
      <input
        ref={importInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportRoadmap(file);
          e.target.value = "";
        }}
      />

      {/* ── Submit contribution confirm modal ── */}
      <Modal
        open={submitConfirmOpen}
        onCancel={() => setSubmitConfirmOpen(false)}
        onOk={handleSubmitContribution}
        okText="Commit all"
        confirmLoading={isSubmitting}
        title="Commit your whole view"
        width={420}
      >
        <p className="text-sm">
          This commits every block of your view at once (you can also commit blocks one at a time with the lock icon on each). Committing carries a stronger "this is my settled view" tag and notifies the owner.
        </p>
        <p className="text-xs text-muted-foreground" style={{ marginTop: 8 }}>
          Your work stays yours — you can move a block back to draft or keep editing anytime. Your content is available to the team as the roadmap moves toward being committed, whether it's draft or committed.
        </p>
      </Modal>

      {/* ── Share & Collaborate modal ── */}
      <Modal
        open={shareModalOpen}
        onCancel={() => setShareModalOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 32 }}>
            <Users style={{ width: 16, height: 16, color: "#2c1561" }} />
            <span>Share &amp; Collaborate</span>
            {(session?.userRole === "owner" || session?.userRole === "advisor") && (
              <Popover
                open={settingsPopoverOpen}
                onOpenChange={setSettingsPopoverOpen}
                trigger="click"
                placement="bottomRight"
                title={<span style={{ fontSize: 13 }}>Sharing Settings</span>}
                content={
                  <div style={{ width: 260, display: "flex", flexDirection: "column", gap: 12 }}>
                    <Checkbox
                      checked={collabSettings.teamMembersCanInvite}
                      onChange={(e) => setCollabSettings((s) => ({ ...s, teamMembersCanInvite: e.target.checked }))}
                    >
                      <span style={{ fontSize: 12 }}>Team members can invite collaborators</span>
                    </Checkbox>
                    <Checkbox
                      checked={collabSettings.notifyOnJoin}
                      onChange={(e) => setCollabSettings((s) => ({ ...s, notifyOnJoin: e.target.checked }))}
                    >
                      <span style={{ fontSize: 12 }}>Notify me when someone joins</span>
                    </Checkbox>
                  </div>
                }
              >
                <Tooltip title="Sharing settings">
                  <AntdButton
                    type="text"
                    size="small"
                    icon={<Settings style={{ width: 14, height: 14 }} />}
                    style={{ marginLeft: "auto", color: "#94a3b8" }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popover>
            )}
          </div>
        }
        footer={null}
        width={520}
      >
        <Tabs
          activeKey={shareModalTab}
          onChange={(k) => setShareModalTab(k as "link" | "invite")}
          items={[
            {
              key: "invite",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <UserPlus style={{ width: 13, height: 13 }} />
                  Invite People
                </span>
              ),
              children: (
                <div className="space-y-4">
                  {session?.userRole !== "owner" && session?.userRole !== "advisor" && (
                    <div className="py-3 px-3 bg-slate-50 rounded-md border border-slate-100 text-xs text-muted-foreground">
                      You don't have permission to invite others to this roadmap. Please contact the owner to request access.
                    </div>
                  )}
                  {(session?.userRole === "owner" || session?.userRole === "advisor") && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Invite collaborators by email — separate multiple addresses with commas.
                    </p>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Input
                        placeholder="name@company.com, another@company.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onPressEnter={handleSendInvite}
                        style={{ flex: 1 }}
                        size="small"
                      />
                      <Select
                        value={inviteRoleGroup}
                        onChange={(v) => setInviteRoleGroup(v)}
                        size="small"
                        style={{ width: 145 }}
                        options={[
                          { value: "owner", label: "Owner" },
                          { value: "team_member", label: "Team Member" },
                        ]}
                      />
                      <AntdButton
                        type="primary"
                        size="small"
                        loading={inviteLoading}
                        onClick={handleSendInvite}
                        disabled={!inviteEmail.trim()}
                      >
                        Send
                      </AntdButton>
                    </div>
                    <div style={{ padding: "4px 0 2px" }}>
                      <Input.TextArea
                        placeholder="Add a personal message to your invitation (optional)"
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        size="small"
                        style={{ fontSize: 12 }}
                      />
                    </div>
                    {inviteError && (
                      <p className="text-xs text-red-500 mt-1">{inviteError}</p>
                    )}
                  </div>
                  )}
                  {collaboratorsLoading ? (
                    <p className="text-xs text-muted-foreground">Loading…</p>
                  ) : session?.userRole === "owner" || session?.userRole === "advisor" || collaborators.length > 0 ? (
                    <div>
                      <Divider style={{ margin: "8px 0" }} />
                      <p className="text-xs font-medium text-muted-foreground mb-2">People with access</p>
                      <div className="space-y-2">
                        {/* Owner / Advisor row — always shown first */}
                        {(session?.userRole === "owner" || session?.userRole === "advisor") && (() => {
                          const myName = vojomoAccount ? (vojomoAccount as unknown as Record<string, string>)["name"] : "";
                          const myEmail = vojomoAccount ? (vojomoAccount as unknown as Record<string, string>)["email"] : "";
                          const isActualOwner = session?.userRole === "owner";
                          const isActualAdvisor = session?.userRole === "advisor";
                          // Check if this user is also the session owner (when viewing as advisor)
                          const hasCoOwner = collaborators.some((c) => c.accessLevel === "owner" && c.status === "accepted");
                          const roleLabel = isActualOwner && isActualAdvisor
                            ? "Owner · Advisor"
                            : isActualOwner ? "Owner" : "Advisor";
                          return (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p className="text-sm truncate">{myName || myEmail}</p>
                                {myName && <p className="text-[11px] text-muted-foreground truncate">{myEmail}</p>}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                <Tag color={isActualOwner ? "purple" : "blue"} className="text-xs">{roleLabel}</Tag>
                                {isActualOwner && (
                                  <Tooltip title={hasCoOwner ? "Leave this roadmap" : "You need a co-owner before you can leave"}>
                                    <AntdButton
                                      type="text"
                                      size="small"
                                      danger
                                      disabled={!hasCoOwner}
                                      onClick={() => handleLeaveSession()}
                                      style={{ padding: "0 4px", height: 22 }}
                                    >
                                      <Trash2 style={{ width: 12, height: 12 }} />
                                    </AntdButton>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {collaborators.map((c) => {
                          const isOwner = session?.userRole === "owner" || session?.userRole === "advisor";
                          const isCollabOwner = c.accessLevel === "owner";
                          const isReviewer = c.accessLevel === "view" || c.accessLevel === "comment";
                          // 3-option group dropdown — Reviewer sub-type shown separately below
                          const groupOptions: { value: string; label: string }[] = [
                            { value: "reviewer", label: "Reviewer" },
                            { value: "solo_contributor", label: "Contributor" },
                            { value: "team_member", label: "Team Member" },
                          ];
                          const groupValue = isReviewer ? "reviewer" : c.accessLevel;
                          const handleGroupChange = (newGroup: string) => {
                            if (newGroup === "reviewer") {
                              handleUpdateCollaboratorRole(c.id, "comment");
                            } else {
                              handleUpdateCollaboratorRole(c.id, newGroup as CollaboratorAccessLevel);
                            }
                          };
                          return (
                            <div key={c.id}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p className="text-sm truncate">{c.inviteeName || c.inviteeEmail}</p>
                                {c.inviteeName && (
                                  <p className="text-[11px] text-muted-foreground truncate">{c.inviteeEmail}</p>
                                )}
                                {c.accessLevel === "solo_contributor" && c.soloSessionId && (
                                  <p className="text-[11px] text-primary">→ Contribution session created</p>
                                )}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                {c.status === "pending" && (
                                  <Tag color="processing" className="text-xs">Pending</Tag>
                                )}
                                {/* Co-owner: show tag + demote button */}
                                {isCollabOwner ? (
                                  <>
                                    <Tag color="purple" className="text-xs">Owner</Tag>
                                    {isOwner && (
                                      <Tooltip title="Demote to Team Member">
                                        <AntdButton
                                          type="text"
                                          size="small"
                                          onClick={() => handleDemoteCollaborator(c.id)}
                                          style={{ padding: "0 4px", height: 22, fontSize: 11, color: "#64748b" }}
                                        >
                                          ↓ Member
                                        </AntdButton>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : isOwner ? (
                                  /* Owner editing: 3-option group dropdown */
                                  <Select
                                    value={groupValue}
                                    size="small"
                                    style={{ width: 130 }}
                                    options={groupOptions}
                                    onChange={handleGroupChange}
                                    suffixIcon={<ChevronDown style={{ width: 11, height: 11 }} />}
                                  />
                                ) : (
                                  /* Non-owner viewing: static role tag */
                                  <Tag
                                    color={
                                      c.accessLevel === "team_member" ? "blue" :
                                      c.accessLevel === "solo_contributor" ? "geekblue" : "default"
                                    }
                                    className="text-xs"
                                  >
                                    {c.accessLevel === "team_member" ? "Team Member" :
                                     c.accessLevel === "solo_contributor" ? "Contributor" : "Reviewer"}
                                  </Tag>
                                )}
                                {/* Promote team_member to owner — owner only */}
                                {isOwner && c.accessLevel === "team_member" && c.status === "accepted" && (
                                  <Tooltip title="Promote to Owner">
                                    <AntdButton
                                      type="text"
                                      size="small"
                                      onClick={() => handlePromoteCollaborator(c.id)}
                                      style={{ padding: "0 4px", height: 22, fontSize: 11, color: "#7c3aed" }}
                                    >
                                      ↑ Owner
                                    </AntdButton>
                                  </Tooltip>
                                )}
                                {isOwner && (
                                  <Tooltip title="Remove">
                                    <AntdButton
                                      type="text"
                                      size="small"
                                      danger
                                      onClick={() => handleRemoveCollaborator(c.id)}
                                      style={{ padding: "0 4px", height: 22 }}
                                    >
                                      <Trash2 style={{ width: 12, height: 12 }} />
                                    </AntdButton>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                              {/* Reviewer sub-choice — appears below when this collaborator is a reviewer */}
                              {isOwner && isReviewer && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0 2px" }}>
                                  <span className="text-xs text-muted-foreground">Reviewer access:</span>
                                  <Select
                                    value={c.accessLevel}
                                    size="small"
                                    style={{ width: 190 }}
                                    options={[
                                      { value: "comment", label: "Can view & comment" },
                                      { value: "view", label: "View only" },
                                    ]}
                                    onChange={(v) => handleUpdateCollaboratorRole(c.id, v as "view" | "comment")}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ),
            },
            {
              key: "link",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Share2 style={{ width: 13, height: 13 }} />
                  Share Link
                </span>
              ),
              children: (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Anyone with this link can view your published roadmap — no account required.
                  </p>
                  {shareUrl ? (
                    <>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Input
                          readOnly
                          value={shareUrl}
                          size="small"
                          style={{ flex: 1, background: "#f8fafc" }}
                        />
                        <AntdButton size="small" onClick={handleCopyShareUrl}>
                          {shareCopied ? (
                            <Check style={{ width: 12, height: 12, color: "#10b981" }} />
                          ) : (
                            <Copy style={{ width: 12, height: 12 }} />
                          )}
                        </AntdButton>
                      </div>
                      {(session?.userRole === "owner" || session?.userRole === "advisor") && (
                        <AntdButton
                          danger
                          type="text"
                          size="small"
                          onClick={handleRevokeShare}
                          icon={<Trash2 style={{ width: 12, height: 12 }} />}
                        >
                          Revoke link
                        </AntdButton>
                      )}
                    </>
                  ) : session?.userRole === "owner" || session?.userRole === "advisor" ? (
                    <>
                      {session?.draftVersion && session.draftVersion > 0 ? (
                        <AntdButton type="primary" size="small" onClick={handleShare}>
                          Generate share link
                        </AntdButton>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Publish your roadmap first to generate a share link.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="py-3 px-3 bg-slate-50 rounded-md border border-slate-100 text-xs text-muted-foreground">
                      The owner has not enabled public sharing for this roadmap. Contact the owner to request it.
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>

      {/* ── Session Settings drawer ── */}
      <Drawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Settings style={{ width: 15, height: 15, color: "#2c1561" }} />
            <span>Session Settings</span>
          </div>
        }
        width={420}
        styles={{ body: { padding: 0 } }}
      >
        <Tabs
          activeKey={settingsTab}
          onChange={(k) => setSettingsTab(k as "advisors" | "teams")}
          size="small"
          style={{ padding: "0 16px" }}
          items={[
            {
              key: "advisors",
              label: "Advisors",
              children: (
                <div style={{ paddingTop: 8 }}>
                  <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
                    Session advisors can view team progress grids, kick off rounds, and manage releases for this session. Add by email — they must already have a Vojomo account.
                  </p>

                  {/* Current advisors list */}
                  {session.advisors.length > 0 ? (
                    <div style={{ marginBottom: 16 }}>
                      {session.advisors.map((a) => (
                        <div
                          key={a.userId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "7px 10px",
                            border: "1px solid #e2e8f0",
                            borderRadius: 7,
                            marginBottom: 6,
                            background: "#fafafa",
                          }}
                        >
                          <div>
                            <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{a.name || a.email}</p>
                            {a.name && <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>{a.email}</p>}
                          </div>
                          <AntdButton
                            size="small"
                            type="text"
                            danger
                            loading={advisorRemovingId === a.userId}
                            icon={<X style={{ width: 12, height: 12 }} />}
                            onClick={() => handleRemoveAdvisor(a.userId)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16, fontStyle: "italic" }}>
                      No session-level advisors yet.
                    </p>
                  )}

                  {/* Add advisor */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <Input
                      placeholder="advisor@example.com"
                      value={advisorEmailInput}
                      onChange={(e) => setAdvisorEmailInput(e.target.value)}
                      onPressEnter={handleAddAdvisor}
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <AntdButton
                      size="small"
                      type="primary"
                      loading={advisorAddLoading}
                      onClick={handleAddAdvisor}
                      style={{ background: "#2c1561", borderColor: "#2c1561" }}
                    >
                      Add
                    </AntdButton>
                  </div>
                  {advisorAddError && (
                    <p style={{ fontSize: 11, color: "#ef4444", marginTop: 6 }}>{advisorAddError}</p>
                  )}
                </div>
              ),
            },
            {
              key: "teams",
              label: "Participant Teams",
              children: (
                <div style={{ paddingTop: 8 }}>
                  <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
                    Named teams for grouping progress contributions in the team grid view.
                  </p>

                  {teamsLoading ? (
                    <p style={{ fontSize: 12, color: "#94a3b8" }}>Loading…</p>
                  ) : participantTeams.length > 0 ? (
                    <div style={{ marginBottom: 16 }}>
                      {participantTeams.map((t) => {
                        const isExpanded = expandedTeamId === t.id;
                        return (
                          <div
                            key={t.id}
                            style={{ border: "1px solid #e2e8f0", borderRadius: 7, marginBottom: 6, overflow: "hidden" }}
                          >
                            {/* Team header row */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: isExpanded ? "#f8fafc" : "#fafafa" }}>
                              <div
                                style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1, minWidth: 0 }}
                                onClick={() => setExpandedTeamId(isExpanded ? null : t.id)}
                              >
                                {isExpanded
                                  ? <ChevronUp style={{ width: 12, height: 12, color: "#64748b", flexShrink: 0 }} />
                                  : <ChevronDown style={{ width: 12, height: 12, color: "#64748b", flexShrink: 0 }} />
                                }
                                <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{t.name}</p>
                                <span style={{ fontSize: 10, color: "#94a3b8" }}>{t.members.length} member{t.members.length !== 1 ? "s" : ""}</span>
                              </div>
                              <AntdButton
                                size="small"
                                type="text"
                                danger
                                loading={teamDeleteId === t.id}
                                icon={<Trash2 style={{ width: 12, height: 12 }} />}
                                onClick={() => handleDeleteTeam(t.id)}
                              />
                            </div>
                            {/* Expanded member management */}
                            {isExpanded && (
                              <div style={{ borderTop: "1px solid #e2e8f0", padding: "8px 10px", background: "white" }}>
                                {t.members.length > 0 ? (
                                  <div style={{ marginBottom: 8 }}>
                                    {t.members.map((m) => (
                                      <div key={m.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                        <span style={{ fontSize: 11, color: "#374151" }}>{m.email}</span>
                                        <AntdButton
                                          size="small"
                                          type="text"
                                          danger
                                          icon={<X style={{ width: 11, height: 11 }} />}
                                          onClick={() => handleRemoveMember(t.id, m.email)}
                                          style={{ height: 20, padding: "0 4px" }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8, fontStyle: "italic" }}>No members yet.</p>
                                )}
                                <div style={{ display: "flex", gap: 5 }}>
                                  <Input
                                    placeholder="member@company.com"
                                    value={memberEmailInputs[t.id] || ""}
                                    onChange={(e) => setMemberEmailInputs((prev) => ({ ...prev, [t.id]: e.target.value }))}
                                    onPressEnter={() => handleAddMember(t.id)}
                                    size="small"
                                    style={{ flex: 1, fontSize: 11 }}
                                  />
                                  <AntdButton
                                    size="small"
                                    type="primary"
                                    loading={memberAddLoading === t.id}
                                    onClick={() => handleAddMember(t.id)}
                                    style={{ background: "#2c1561", borderColor: "#2c1561", fontSize: 11 }}
                                  >
                                    Add
                                  </AntdButton>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16, fontStyle: "italic" }}>
                      No teams yet.
                    </p>
                  )}

                  <div style={{ display: "flex", gap: 6 }}>
                    <Input
                      placeholder="Team name"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      onPressEnter={handleAddTeam}
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <AntdButton
                      size="small"
                      type="primary"
                      loading={teamAddLoading}
                      onClick={handleAddTeam}
                      style={{ background: "#2c1561", borderColor: "#2c1561" }}
                    >
                      Add
                    </AntdButton>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      {/* ── Research modal ── */}
      {researchModalOpen && (() => {
        const runs: ResearchRun[] = [...(session?.researchRuns ?? [])].reverse();
        return (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)", padding: 16 }}
            onClick={() => { if (!researchRefreshing) setResearchModalOpen(false); }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(15,23,42,0.16)", border: "1px solid #e2e8f0", width: "100%", maxWidth: 460, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              {/* Header */}
              <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Globe style={{ width: 16, height: 16, color: "#2c1561" }} />
                    <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, fontFamily: '"Albert Sans", sans-serif', color: "#0f172a" }}>
                      Research
                    </h2>
                  </div>
                  <button
                    onClick={() => setResearchModalOpen(false)}
                    disabled={researchRefreshing}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex" }}
                  >
                    <X style={{ width: 16, height: 16 }} />
                  </button>
                </div>
                <p style={{ fontSize: 12, color: "#64748b", margin: 0, lineHeight: 1.5 }}>
                  Runs fresh external research in the background. The roadmap updates automatically when complete.
                </p>
              </div>

              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
                {/* Run form */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>
                    Additional context{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <Input.TextArea
                    rows={3}
                    placeholder="New URLs, recent news, updated strategy, or anything else to include…"
                    value={researchAdditionalContext}
                    onChange={(e) => { setResearchAdditionalContext(e.target.value); setResearchRefreshStatus("idle"); }}
                    disabled={researchRefreshing || researchRefreshStatus === "started"}
                    style={{ fontSize: 12, resize: "none" }}
                  />
                </div>

                {researchRefreshStatus === "started" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #bbf7d0", marginBottom: 14 }}>
                    <Globe style={{ width: 14, height: 14, color: "#16a34a", flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#166534", margin: 0 }}>Research underway</p>
                      <p style={{ fontSize: 11, color: "#166534", margin: "1px 0 0" }}>Running in the background — the roadmap will update in about 2 minutes.</p>
                    </div>
                  </div>
                )}

                {researchRefreshStatus === "error" && (
                  <p style={{ fontSize: 12, color: "#ef4444", marginBottom: 12 }}>
                    Failed to start research. Please try again.
                  </p>
                )}

                <div style={{ display: "flex", gap: 8, marginBottom: runs.length > 0 ? 24 : 0 }}>
                  <AntdButton
                    style={{ flex: 1 }}
                    onClick={() => setResearchModalOpen(false)}
                    disabled={researchRefreshing}
                  >
                    {researchRefreshStatus === "started" ? "Close" : "Cancel"}
                  </AntdButton>
                  <AntdButton
                    type="primary"
                    style={{ flex: 1, background: "#2c1561", borderColor: "#2c1561" }}
                    loading={researchRefreshing}
                    onClick={handleResearchRefresh}
                    disabled={researchRefreshStatus === "started"}
                  >
                    {researchRefreshing ? "Starting…" : "Run Research"}
                  </AntdButton>
                </div>

                {/* Research history */}
                {runs.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", marginBottom: 10 }}>
                      Research History
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {runs.map((run, i) => {
                        const date = new Date(run.triggeredAt);
                        const isRunning = !run.completedAt && i === 0;
                        return (
                          <div
                            key={run.triggeredAt}
                            style={{ borderRadius: 8, border: `1px solid ${isRunning ? "#fde68a" : "#e2e8f0"}`, background: isRunning ? "#fffbeb" : "#fafbff", padding: "10px 12px" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: run.summary ? 6 : 0 }}>
                              {isRunning ? (
                                <Loader2 style={{ width: 11, height: 11, color: "#d97706", flexShrink: 0 }} className="animate-spin" />
                              ) : (
                                <CheckCircle2 style={{ width: 11, height: 11, color: "#16a34a", flexShrink: 0 }} />
                              )}
                              <span style={{ fontSize: 11, color: isRunning ? "#92400e" : "#374151", fontWeight: 500 }}>
                                {isRunning ? "Running…" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " · " + date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                              </span>
                              <span style={{
                                marginLeft: "auto", fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 999,
                                background: run.triggerSource === "auto" ? "#f1f5f9" : "#ede9fe",
                                color: run.triggerSource === "auto" ? "#64748b" : "#5b21b6",
                              }}>
                                {run.triggerSource === "auto" ? "Auto" : "Manual"}
                              </span>
                            </div>
                            {run.additionalContext && (
                              <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 4px", fontStyle: "italic" }}>
                                Context: {run.additionalContext}
                              </p>
                            )}
                            {run.summary ? (
                              <p style={{ fontSize: 12, color: "#374151", margin: 0, lineHeight: 1.55 }}>
                                {run.summary}
                              </p>
                            ) : isRunning ? (
                              <p style={{ fontSize: 11, color: "#92400e", margin: 0 }}>
                                Gathering data and updating roadmap…
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Export modal ── */}
      {showExportModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.45)",
            padding: 16,
          }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 8px 32px rgba(15,23,42,0.16)",
              border: "1px solid #e2e8f0",
              padding: 24,
              width: "100%",
              maxWidth: 360,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Export as PDF</h2>
            <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              Choose what to include. Opens in a new tab — click "Save as PDF" to download.
            </p>

            {/* View selector — Solo / Team / Both */}
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: "#64748b", marginBottom: 8 }}>
                View
              </p>
              <div style={{ display: "flex", gap: 6 }}>
                {(
                  [
                    { value: "both" as const, label: "Both" },
                    { value: "solo" as const, label: "Solo" },
                    { value: "team" as const, label: "Team" },
                  ]
                ).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setExportView(opt.value)}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: 6,
                      border: `1px solid ${exportView === opt.value ? "#6366f1" : "#e2e8f0"}`,
                      background: exportView === opt.value ? "#f0f0ff" : "#fff",
                      color: exportView === opt.value ? "#4338ca" : "#0f172a",
                      fontSize: 12,
                      fontWeight: exportView === opt.value ? 600 : 400,
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {(
                [
                  { value: "both" as const, label: "Complete Report", desc: "Roadmap + full chat transcript" },
                  { value: "roadmap" as const, label: "Roadmap Only", desc: "All 7 building blocks, gaps & questions" },
                  { value: "chat" as const, label: "Chat Transcript", desc: "Full conversation with Vo" },
                ]
              ).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setExportMode(opt.value)}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: `1px solid ${exportMode === opt.value ? "#6366f1" : "#e2e8f0"}`,
                    background: exportMode === opt.value ? "#f0f0ff" : "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `2px solid ${exportMode === opt.value ? "#6366f1" : "#94a3b8"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {exportMode === opt.value && (
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1" }} />
                    )}
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 500, marginBottom: 1 }}>{opt.label}</p>
                    <p style={{ fontSize: 11, color: "#64748b" }}>{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <AntdButton
                type="text"
                style={{ flex: 1, height: 36 }}
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </AntdButton>
              <AntdButton
                type="primary"
                style={{ flex: 1, height: 36, gap: 6 }}
                onClick={() => {
                  handleExportPDF(exportMode, exportView);
                  setShowExportModal(false);
                }}
              >
                <FileText style={{ width: 13, height: 13 }} />
                Open Document
              </AntdButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function RoadmapBuilderSession() {
  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      <MicRecorderProvider>
        <ModusProvider>
          <RoadmapBuilderSessionInner />
        </ModusProvider>
      </MicRecorderProvider>
    </ConfigProvider>
  );
}
